import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import RichTextEditor from './RichTextEditor';
import Table1_2 from './Table1_2';
import {
  LogOut, Edit, Trash2, Search, X, Save, User, AlertCircle, CheckCircle, Clock, Calendar,
  FileText, FileSpreadsheet, Briefcase, Key, Plus, Check, ChevronUp, ChevronDown, Filter, PlayCircle,
  Users, MapPin, RotateCcw
} from 'lucide-react';
import ExportButtons from './btns/ExportButtons';
import * as XLSX from 'xlsx';
import { 
  Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, 
  Table, TableRow, TableCell, WidthType, BorderStyle, PageBreak 
} from 'docx';
import { saveAs } from 'file-saver';
import marabesLogo from '../assets/images/logos/Marabes.png';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  useTheme,
  InputAdornment
} from '@mui/material';

import PageHeader from './headers/Header';
import {
  getAllEmployees,
  getAllOffboardings,
  startOffboarding,
  updateOffboarding,
  deleteOffboarding,
  getOffboardingById,
  getEmployeeImage
} from '../services/HRstaff';
import { getFinalPayStatement, getOffboardedSalaryReport } from '../services/reportingAPIs';
import { getAllInactiveEmployees } from '../services/EmployeeService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
if (typeof jsPDF === 'function' && !jsPDF.prototype.autoTable) {
  jsPDF.prototype.autoTable = autoTable;
}

const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url);
    return null;
  }
};

const MANDATORY_ITEMS = [
  { key: 'directLeave', label: 'Set Inactive', icon: CheckCircle },
  { key: 'exitInterviewConducted', label: 'Exit Interview Conducted', icon: CheckCircle },
  { key: 'emailOfResignationReceived', label: 'Email of Resignation Received', icon: FileText },
  { key: 'recordsUpdatedInHrSystems', label: 'Records Updated in HR Systems', icon: FileText },
  { key: 'allAccessRevoked', label: 'All Access Revoked (IT, Building, Systems, etc.)', icon: Key },
  { key: 'communicationChannelsRemoved', label: 'Communication Channels Removed (Slack, Teams, Email Groups, etc.)', icon: Key },
  { key: 'resignationLetterReceived', label: 'Resignation Letter Received (Official Signed Document)', icon: FileText },
  { key: 'departureAnnouncementSent', label: 'Departure Announcement Sent (Team Notification)', icon: FileText },
  { key: 'leaveBalanceSettlementSigned', label: 'Leave Balance Settlement Signed', icon: FileText },
  { key: 'offboardingMeetingConducted', label: 'Offboarding Meeting Conducted', icon: CheckCircle },
  { key: 'companyAssetsReturned', label: 'Company Assets Returned (Laptop, Monitor, Phone, Badge, etc.)', icon: Briefcase },
];

const OPTIONAL_ITEMS = [
  { key: 'knowledgeTransfer', label: 'Knowledge Transfer Completed', icon: Briefcase },
];

const ALUMNI_ITEMS = [
  { key: 'alumni', label: 'Alumni Group', icon: User },
];

const ALL_CHECKLIST_KEYS = [...MANDATORY_ITEMS, ...OPTIONAL_ITEMS, ...ALUMNI_ITEMS].map(i => i.key);

const Offboarding = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [offboardingRecords, setOffboardingRecords] = useState([]);
  const [originalRecords, setOriginalRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inactiveEmployees, setInactiveEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [empSearchQuery, setEmpSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [filterBy, setFilterBy] = useState('all');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [showSuccess, setShowSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);
  const [stcDialog, setStcDialog] = useState({ open: false, record: null });
  const [stcPreview, setStcPreview] = useState(null); // computed preview data
  const [stcTab, setStcTab] = useState('form');

  const [activeTable, setActiveTable] = useState('in-progress');

  const tableWrapperRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isLocalUpdateRef = useRef(false);
  const exportContentRef = useRef(null);
  const [exportTableData, setExportTableData] = useState(null);

  const [inProgressSortBy, setInProgressSortBy] = useState('dateOfDeparture');
  const [inProgressSortOrder, setInProgressSortOrder] = useState('desc');
  const [completedSortBy, setCompletedSortBy] = useState('dateOfDeparture');
  const [completedSortOrder, setCompletedSortOrder] = useState('desc');
  const [showAuditInfo, setShowAuditInfo] = useState(false);

  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingInProgressPdf, setIsExportingInProgressPdf] = useState(false);
  const [exportDialog, setExportDialog] = useState({ open: false, type: '' });

  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const MONTH_NAMES = ['All', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const initialFormData = {
    employeeId: '', employeeName: '', dateOfDeparture: '', reason: '',
    directLeave: false,
    exitInterviewConducted: false,
    emailOfResignationReceived: false,
    resignationLetterReceived: false,
    offboardingMeetingConducted: false,
    departureAnnouncementSent: false,
    knowledgeTransfer: false,
    companyAssetsReturned: false,
    recordsUpdatedInHrSystems: false,
    leaveBalanceSettlementSigned: false,
    allAccessRevoked: false,
    communicationChannelsRemoved: false,
    alumni: false,
    vacation: '', notes: '',
    annualVacationEntitlement: '', vacationTaken: '', remainingVacation: ''
  };
  const [formData, setFormData] = useState(initialFormData);

  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    let dateObj = typeof date === 'string' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) {
      dateObj = new Date(Date.parse(date));
    }
    if (isNaN(dateObj.getTime())) return 'N/A';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatAuditDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatUnderscoredText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const getEmployeeIdentifier = (employee) => employee?.employeeId || employee?.id || '';

  const getEmployeeFullName = (employee) => (
    `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim()
  );

  const findEmployeeByIdentifier = (value, activeList = employees, inactiveList = inactiveEmployees) => (
    activeList.find(emp => emp.employeeId === value || emp.id === value) ||
    inactiveList.find(emp => emp.employeeId === value || emp.id === value)
  );

  const findEmployeeByName = (employeeName, activeList = employees, inactiveList = inactiveEmployees) => (
    activeList.find(emp => getEmployeeFullName(emp) === employeeName) ||
    inactiveList.find(emp => getEmployeeFullName(emp) === employeeName)
  );

  const mapOffboardingRecord = (record, activeList = employees, inactiveList = inactiveEmployees, fallback = {}) => {
    if (!record) return null;

    const id = record.offboardingId ?? record.id ?? fallback.id ?? null;
    if (!id) return null;

    const employeeId = record.employeeId ?? fallback.employeeId ?? '';
    const employeeName = `${record.employeeFirstName ?? fallback.employeeFirstName ?? ''} ${record.employeeLastName ?? fallback.employeeLastName ?? ''}`.trim()
      || fallback.employeeName
      || 'Unknown';

    // Offboarding records may not include employeeId, so resolve it from employee details when possible.
    const employee = activeList.find(emp =>
      (emp.id && emp.id === employeeId) || 
      (emp.employeeId && emp.employeeId === employeeId) ||
      getEmployeeFullName(emp) === employeeName
    );

    const inactiveEmp = inactiveList.find(emp =>
      (emp.id && emp.id === employeeId) ||
      (emp.employeeId && emp.employeeId === employeeId) ||
      getEmployeeFullName(emp) === employeeName
    );

    const sourceEmp = employee || inactiveEmp;
    const resolvedEmployeeId = getEmployeeIdentifier(sourceEmp) || record.employeeId || fallback.employeeId || '';

    let joinDate = fallback.dateOfJoining || fallback.joiningDate || fallback.startDate || record.dateOfJoining || record.joiningDate || record.startDate || null;
    if (sourceEmp) {
      joinDate = sourceEmp.dateOfJoining || sourceEmp.joiningDate || sourceEmp.startDate || joinDate;
    }

    let officeValue = record.employeeOfficeName || fallback.employeeOfficeName || fallback.office || 'Not Specified';
    let departmentValue = record.employeeDepartmentName || fallback.employeeDepartmentName || fallback.department || 'Not Specified';
    if (sourceEmp) {
      if ((officeValue === 'Not Specified' || officeValue === 'N/A') && sourceEmp.office) {
        officeValue = typeof sourceEmp.office === 'object' ? sourceEmp.office.name : sourceEmp.office;
      }
      if ((departmentValue === 'Not Specified' || departmentValue === 'N/A') && sourceEmp.department) {
        departmentValue = typeof sourceEmp.department === 'object' ? sourceEmp.department.name : sourceEmp.department;
      }
    }

    const checklist = {
      directLeave: Boolean(record.directLeave ?? fallback.directLeave),
      exitInterviewConducted: Boolean(record.exitInterviewConducted ?? fallback.exitInterviewConducted),
      emailOfResignationReceived: Boolean(record.emailOfResignationReceived ?? fallback.emailOfResignationReceived),
      resignationLetterReceived: Boolean(record.resignationLetterReceived ?? fallback.resignationLetterReceived),
      offboardingMeetingConducted: Boolean(record.offboardingMeetingConducted ?? fallback.offboardingMeetingConducted),
      departureAnnouncementSent: Boolean(record.departureAnnouncementSent ?? fallback.departureAnnouncementSent),
      knowledgeTransfer: Boolean(record.knowledgeTransferCompleted ?? fallback.knowledgeTransfer),
      recordsUpdatedInHrSystems: Boolean(
        record.recordsUpdated ?? record.recordsUpdatedInHrSystems ?? fallback.recordsUpdatedInHrSystems
      ),
      leaveBalanceSettlementSigned: Boolean(record.leaveBalanceSettlementSigned ?? fallback.leaveBalanceSettlementSigned),
      companyAssetsReturned: Boolean(record.companyAssetsReturned ?? fallback.companyAssetsReturned),
      allAccessRevoked: Boolean(record.allAccessRevoked ?? fallback.allAccessRevoked),
      communicationChannelsRemoved: Boolean(record.communicationChannelsRemoved ?? fallback.communicationChannelsRemoved),
      alumni: Boolean(record.alumni ?? fallback.alumni)
    };

    const resolvedProgress = Number(record.progress ?? fallback.progress ?? 0);
    const resolvedStatus = resolvedProgress >= 100
      ? 'COMPLETED'
      : (record.status || fallback.status || 'IN_PROGRESS');

    return {
      id,
      employeeName,
      department: departmentValue,
      office: officeValue,
      dateOfDeparture: record.exitDate || fallback.exitDate || '',
      dateOfJoining: joinDate,
      reason: formatUnderscoredText(record.reasonForDeparture || fallback.reasonForDeparture || fallback.reason) || 'Not Specified',
      notes: record.additionalNotes || fallback.additionalNotes || fallback.notes || 'Not Specified',
      progress: resolvedProgress,
      status: resolvedStatus.toString().toLowerCase().replace('_', '-'),
      createdBy: record.createdBy || fallback.createdBy || 'SYSTEM',
      createdDate: record.createdDate || fallback.createdDate || record.offboardingDate || fallback.offboardingDate,
      lastModifiedBy: record.lastModifiedBy || fallback.lastModifiedBy || record.createdBy || 'SYSTEM',
      lastModifiedDate: record.lastModifiedDate || fallback.lastModifiedDate || record.createdDate || fallback.createdDate || record.offboardingDate,
      offboardingData: {
        employeeId: resolvedEmployeeId,
        employeeName,
        department: departmentValue,
        office: officeValue,
        dateOfDeparture: record.exitDate || fallback.exitDate || '',
        reason: formatUnderscoredText(record.reasonForDeparture || fallback.reasonForDeparture || fallback.reason) || '',
        ...checklist,
        vacation: record.vacationSettlementNotes || fallback.vacation || '',
        notes: record.additionalNotes || fallback.additionalNotes || fallback.notes || '',
        annualVacationEntitlement: (record.annualVacationEntitlement ?? fallback.annualVacationEntitlement ?? 0).toString(),
        vacationTaken: (record.vacationTaken ?? fallback.vacationTaken ?? 0).toString(),
        remainingVacation: (record.remainingVacation ?? fallback.remainingVacation ?? 0).toString()
      },
      ...checklist
    };
  };

  const availableYears = useMemo(() => {
    const years = new Set();
    offboardingRecords.forEach(record => {
      if (record.dateOfDeparture) {
        const year = new Date(record.dateOfDeparture).getFullYear();
        if (!isNaN(year)) years.add(year.toString());
      }
    });
    return ['All', ...Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))];
  }, [offboardingRecords]);

  const filteredActiveEmployees = useMemo(() => {
    const query = empSearchQuery.trim().toLowerCase();
    const selectedEmp = findEmployeeByIdentifier(formData.employeeId);
    const selectedName = selectedEmp ? getEmployeeFullName(selectedEmp).toLowerCase() : '';
    const sorted = [...employees].sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
    if (!query || query === selectedName) return sorted;
    return sorted.filter(emp => {
      const isSelected = emp.employeeId === formData.employeeId;
      const matchesQuery = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase().includes(query) ||
        (emp.employeeId || '').toLowerCase().includes(query);
      return isSelected || matchesQuery;
    });
  }, [employees, empSearchQuery, formData.employeeId]);

  const filteredInactiveEmployees = useMemo(() => {
    const query = empSearchQuery.trim().toLowerCase();
    const selectedEmp = findEmployeeByIdentifier(formData.employeeId);
    const selectedName = selectedEmp ? getEmployeeFullName(selectedEmp).toLowerCase() : '';
    const sorted = [...inactiveEmployees].sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
    if (!query || query === selectedName) return sorted;
    return sorted.filter(emp => {
      const empId = emp.employeeId || emp.id;
      const isSelected = empId === formData.employeeId;
      const matchesQuery = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase().includes(query) ||
        (empId || '').toLowerCase().includes(query);
      return isSelected || matchesQuery;
    });
  }, [inactiveEmployees, empSearchQuery, formData.employeeId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        if (formData.employeeId) {
          const selectedEmp = findEmployeeByIdentifier(formData.employeeId);
          if (selectedEmp) {
            setEmpSearchQuery(getEmployeeFullName(selectedEmp));
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [formData.employeeId, employees, inactiveEmployees]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const [activeData, offboardingData, inactiveData] = await Promise.all([
          getAllEmployees(),
          getAllOffboardings(),
          getAllInactiveEmployees()
        ]);

        console.log('offboarding Data:', offboardingData);

        setEmployees(activeData || []);
        setInactiveEmployees(inactiveData || []);

        const transformedOffboardings = offboardingData
          .map(record => mapOffboardingRecord(record, activeData, inactiveData))
          .filter(record => record !== null);
        setOffboardingRecords(transformedOffboardings);
        setOriginalRecords(JSON.parse(JSON.stringify(transformedOffboardings)));
      } catch (error) {
        console.error('Fetch error:', error);
        setErrorMessage('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCheckboxChange = (recordId, itemKey) => {
    if (tableWrapperRef.current) {
      scrollPositionRef.current = tableWrapperRef.current.scrollLeft;
    }
    isLocalUpdateRef.current = true;
    setOffboardingRecords(prev =>
      prev.map(r =>
        r.id === recordId ? { ...r, [itemKey]: !r[itemKey] } : r
      )
    );
  };

  const hasUnsavedChanges = useCallback(() => {
    return offboardingRecords.some((record) => {
      const original = originalRecords.find(o => o.id === record.id);
      if (!original) return false;
      return ALL_CHECKLIST_KEYS.some(key => record[key] !== original[key]);
    });
  }, [offboardingRecords, originalRecords]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const saveResults = await Promise.all(offboardingRecords.map(async (record) => {
        const original = originalRecords.find(o => o.id === record.id);
        if (!original) return { record, savedRecord: null, changed: false };
        const changed = ALL_CHECKLIST_KEYS.some(key => record[key] !== original[key]);
        if (!changed) return { record, savedRecord: null, changed: false };
        const updatedData = {
          directLeave: record.directLeave,
          exitInterviewConducted: record.exitInterviewConducted,
          emailOfResignationReceived: record.emailOfResignationReceived,
          recordsUpdated: record.recordsUpdated ?? record.recordsUpdatedInHrSystems,
          recordsUpdatedInHrSystems: record.recordsUpdatedInHrSystems,
          allAccessRevoked: record.allAccessRevoked,
          communicationChannelsRemoved: record.communicationChannelsRemoved,
          resignationLetterReceived: record.resignationLetterReceived,
          departureAnnouncementSent: record.departureAnnouncementSent,
          leaveBalanceSettlementSigned: record.leaveBalanceSettlementSigned,
          offboardingMeetingConducted: record.offboardingMeetingConducted,
          companyAssetsReturned: record.companyAssetsReturned,
          knowledgeTransferCompleted: record.knowledgeTransfer,
          alumni: record.alumni,
          exitDate: record.dateOfDeparture,
          reasonForDeparture: record.reason,
          additionalNotes: record.notes,
        };
        const savedRecord = await updateOffboarding(record.id, updatedData);
        return { record, savedRecord, changed: true };
      }));
      isLocalUpdateRef.current = false;

      const normalizedRecords = saveResults.map(({ record, savedRecord, changed }) => {
        if (!changed) return record;
        const mappedRecord = mapOffboardingRecord(
          {
            ...savedRecord,
            offboardingId: savedRecord?.offboardingId ?? savedRecord?.id ?? record.id
          },
          employees,
          inactiveEmployees,
          record
        );
        return mappedRecord || record;
      });

      setOffboardingRecords(normalizedRecords);
      setOriginalRecords(JSON.parse(JSON.stringify(normalizedRecords)));
    } catch (err) {
      console.error('Save failed:', err);
      setErrorMessage('Failed to save checklist changes.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (tableWrapperRef.current && isLocalUpdateRef.current) {
      requestAnimationFrame(() => {
        tableWrapperRef.current.scrollLeft = scrollPositionRef.current;
        isLocalUpdateRef.current = false;
      });
    }
  }, [offboardingRecords]);

  const getFilteredDepartments = () => ['all', ...new Set(offboardingRecords.map(e => e.department))];
  const getFilteredOffices = () => ['all', ...new Set(offboardingRecords.map(e => e.office || 'Not Specified').filter(o => o && o !== 'Not Specified'))];

  const getFilteredRecords = (status) => {
    return offboardingRecords.filter(emp => {
      const matchesSearch = emp.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department.toLowerCase().includes(searchTerm.toLowerCase()) || (emp.office || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.createdBy && emp.createdBy.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.lastModifiedBy && emp.lastModifiedBy.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFilter = filterBy === 'all' || emp.department === filterBy;
      const matchesOffice = selectedOffice === 'all' || emp.office === selectedOffice;
      const matchesStatus = emp.status === status || emp.status.replace('_', '-') === status;

      let matchesYear = true;
      let matchesMonth = true;
      let matchesDateRange = true;

      if (emp.dateOfDeparture) {
        const exitDate = new Date(emp.dateOfDeparture);
        const isValidDate = !isNaN(exitDate.getTime());

        if (isValidDate) {
          if (selectedYear !== 'All') {
            matchesYear = exitDate.getFullYear().toString() === selectedYear;
          }
          if (selectedMonth !== 'All') {
            const monthIndex = MONTH_NAMES.indexOf(selectedMonth) - 1;
            if (monthIndex >= 0) {
              matchesMonth = exitDate.getMonth() === monthIndex;
            }
          }
          if (dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            matchesDateRange = exitDate >= startDate && exitDate <= endDate;
          }
        }
      } else if (selectedYear !== 'All' || selectedMonth !== 'All' || (dateRange.start && dateRange.end)) {
        return false;
      }

      return matchesSearch && matchesFilter && matchesOffice && matchesStatus && matchesYear && matchesMonth && matchesDateRange;
    });
  };

  const handleSort = (field, tableType) => {
    if (tableType === 'in-progress') {
      if (inProgressSortBy === field) setInProgressSortOrder(inProgressSortOrder === 'asc' ? 'desc' : 'asc');
      else { setInProgressSortBy(field); setInProgressSortOrder('desc'); }
    } else {
      if (completedSortBy === field) setCompletedSortOrder(completedSortOrder === 'asc' ? 'desc' : 'asc');
      else { setCompletedSortBy(field); setCompletedSortOrder('desc'); }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'employeeId') {
      const selectedEmployee = findEmployeeByIdentifier(value);
      setFormData(prev => ({
        ...prev,
        employeeId: getEmployeeIdentifier(selectedEmployee) || value,
        employeeName: selectedEmployee
          ? getEmployeeFullName(selectedEmployee)
          : prev.employeeName,
        department: selectedEmployee
          ? selectedEmployee.department?.name || selectedEmployee.department || 'Not Specified'
          : prev.department || 'Not Specified',
        office: selectedEmployee
          ? selectedEmployee.office?.name || selectedEmployee.office || 'Not Specified'
          : prev.office || 'Not Specified'
      }));
    }
  };

  const calculateRemainingVacation = () => {
    const entitlement = parseFloat(formData.annualVacationEntitlement) || 0;
    const taken = parseFloat(formData.vacationTaken) || 0;
    const remaining = entitlement - taken;
    if (remaining !== parseFloat(formData.remainingVacation)) handleInputChange('remainingVacation', remaining.toString());
    return remaining;
  };

  useEffect(() => { calculateRemainingVacation(); }, [formData.annualVacationEntitlement, formData.vacationTaken]);

  const validateForm = () => {
    const errors = {};
    if (!formData.employeeId && !editingId) errors.employeeId = 'Employee selection is required';
    if (!formData.dateOfDeparture) errors.dateOfDeparture = 'Date of departure is required';
    else if (isNaN(new Date(formData.dateOfDeparture).getTime())) errors.dateOfDeparture = 'Invalid date of departure';    
    const stripHtml = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
    };
    
    if (!formData.reason || stripHtml(formData.reason).trim() === '') errors.reason = 'Reason for departure is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { setIsSubmitting(false); return; }
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const selectedEmployee = findEmployeeByIdentifier(formData.employeeId) || findEmployeeByName(formData.employeeName);
      const employeeIdForRequest = getEmployeeIdentifier(selectedEmployee) || formData.employeeId;
      const employeeNameForRequest = getEmployeeFullName(selectedEmployee) || formData.employeeName;
      const offboardingData = {
        exitDate: formData.dateOfDeparture, reasonForDeparture: formData.reason || null,
        employeeFirstName: employeeNameForRequest.split(' ')[0] || '', employeeLastName: employeeNameForRequest.split(' ').slice(1).join(' ') || '',
        employeeDepartmentName: selectedEmployee?.department?.name || selectedEmployee?.department || formData.department || 'Not Specified',
        employeeOfficeName: selectedEmployee?.office?.name || selectedEmployee?.office || formData.office || 'Not Specified',
        annualVacationEntitlement: parseInt(formData.annualVacationEntitlement) || 0, vacationTaken: parseInt(formData.vacationTaken) || 0,
        remainingVacation: parseInt(formData.remainingVacation) || 0, vacationSettlementNotes: formData.vacation || '',
        additionalNotes: formData.notes || '',
        directLeave: Boolean(formData.directLeave),
        exitInterviewConducted: Boolean(formData.exitInterviewConducted),
        emailOfResignationReceived: Boolean(formData.emailOfResignationReceived),
        recordsUpdated: Boolean(formData.recordsUpdatedInHrSystems),
        recordsUpdatedInHrSystems: Boolean(formData.recordsUpdatedInHrSystems),
        allAccessRevoked: Boolean(formData.allAccessRevoked),
        communicationChannelsRemoved: Boolean(formData.communicationChannelsRemoved),
        resignationLetterReceived: Boolean(formData.resignationLetterReceived),
        departureAnnouncementSent: Boolean(formData.departureAnnouncementSent),
        leaveBalanceSettlementSigned: Boolean(formData.leaveBalanceSettlementSigned),
        offboardingMeetingConducted: Boolean(formData.offboardingMeetingConducted),
        companyAssetsReturned: Boolean(formData.companyAssetsReturned),
        knowledgeTransferCompleted: Boolean(formData.knowledgeTransfer),
        alumni: Boolean(formData.alumni),
      };
      const savedRecord = editingId
        ? await updateOffboarding(editingId, offboardingData)
        : await startOffboarding(employeeIdForRequest, offboardingData);

      const existingRecord = editingId
        ? offboardingRecords.find(record => record.id === editingId)
        : null;

      const mappedRecord = mapOffboardingRecord(
        {
          ...offboardingData,
          ...savedRecord,
          offboardingId: savedRecord?.offboardingId ?? savedRecord?.id ?? editingId
        },
        employees,
        inactiveEmployees,
        {
          ...existingRecord,
          id: existingRecord?.id ?? savedRecord?.offboardingId ?? savedRecord?.id ?? editingId,
          employeeId: employeeIdForRequest,
          employeeName: employeeNameForRequest,
          employeeFirstName: employeeNameForRequest.split(' ')[0] || '',
          employeeLastName: employeeNameForRequest.split(' ').slice(1).join(' ') || '',
          employeeDepartmentName: selectedEmployee?.department?.name || selectedEmployee?.department || formData.department || 'Not Specified',
          employeeOfficeName: selectedEmployee?.office?.name || selectedEmployee?.office || formData.office || 'Not Specified',
          exitDate: formData.dateOfDeparture,
          reasonForDeparture: formData.reason || null,
          directLeave: Boolean(formData.directLeave),
          exitInterviewConducted: Boolean(formData.exitInterviewConducted),
          emailOfResignationReceived: Boolean(formData.emailOfResignationReceived),
          resignationLetterReceived: Boolean(formData.resignationLetterReceived),
          offboardingMeetingConducted: Boolean(formData.offboardingMeetingConducted),
          departureAnnouncementSent: Boolean(formData.departureAnnouncementSent),
          knowledgeTransfer: Boolean(formData.knowledgeTransfer),
          recordsUpdated: Boolean(formData.recordsUpdatedInHrSystems),
          recordsUpdatedInHrSystems: Boolean(formData.recordsUpdatedInHrSystems),
          leaveBalanceSettlementSigned: Boolean(formData.leaveBalanceSettlementSigned),
          companyAssetsReturned: Boolean(formData.companyAssetsReturned),
          allAccessRevoked: Boolean(formData.allAccessRevoked),
          communicationChannelsRemoved: Boolean(formData.communicationChannelsRemoved),
          alumni: Boolean(formData.alumni),
          vacation: formData.vacation || '',
          notes: formData.notes || '',
          annualVacationEntitlement: formData.annualVacationEntitlement || '0',
          vacationTaken: formData.vacationTaken || '0',
          remainingVacation: formData.remainingVacation || '0',
          status: savedRecord?.status ?? existingRecord?.status ?? (Number(savedRecord?.progress ?? existingRecord?.progress ?? 0) >= 100 ? 'COMPLETED' : 'IN_PROGRESS'),
          progress: savedRecord?.progress ?? existingRecord?.progress ?? 0,
          createdBy: savedRecord?.createdBy ?? existingRecord?.createdBy ?? 'SYSTEM',
          createdDate: savedRecord?.createdDate ?? existingRecord?.createdDate ?? savedRecord?.offboardingDate ?? existingRecord?.createdDate,
          lastModifiedBy: savedRecord?.lastModifiedBy ?? savedRecord?.createdBy ?? existingRecord?.lastModifiedBy ?? 'SYSTEM',
          lastModifiedDate: savedRecord?.lastModifiedDate ?? existingRecord?.lastModifiedDate ?? savedRecord?.createdDate ?? existingRecord?.createdDate ?? savedRecord?.offboardingDate
        }
      );

      if (mappedRecord) {
        const exists = offboardingRecords.some(record => record.id === mappedRecord.id);
        const nextRecords = exists
          ? offboardingRecords.map(record => (record.id === mappedRecord.id ? mappedRecord : record))
          : [mappedRecord, ...offboardingRecords];
        setOffboardingRecords(nextRecords);
        setOriginalRecords(JSON.parse(JSON.stringify(nextRecords)));
      }

      resetForm();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (error) {
      console.error('[Offboarding] Save failed:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message
      });

      const status = error?.response?.status;
      let userMessage = 'Something went wrong while saving. Please try again.';
      if (status === 400) {
        userMessage = 'Some required fields are missing or invalid. Please check the form and try again.';
      } else if (status === 409) {
        userMessage = 'This employee already has an active offboarding record.';
      } else if (status === 500) {
        userMessage = 'A server error occurred. Please make sure all required fields (including Reason for Departure) are filled in correctly, then try again.';
      } else if (!status) {
        userMessage = 'Unable to reach the server. Please check your connection and try again.';
      }
      setErrorMessage(userMessage);
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setEditingId(null);
    setShowForm(false);
    setIsSubmitting(false);
    setEmpSearchQuery('');
  };

  const handleEdit = async (employee) => {
    setIsLoading(true);
    try {
      const offboardingData = await getOffboardingById(employee.id);
      const employeeName = `${offboardingData.employeeFirstName || ''} ${offboardingData.employeeLastName || ''}`.trim() || 'Unknown';
      const selectedEmployee = findEmployeeByIdentifier(offboardingData.employeeId) || findEmployeeByName(employeeName);

      setFormData({
        employeeId: getEmployeeIdentifier(selectedEmployee) || offboardingData.employeeId || '',
        employeeName, department: offboardingData.employeeDepartmentName || 'Not Specified',
        office: offboardingData.employeeOfficeName || 'Not Specified', dateOfDeparture: offboardingData.exitDate || '',
        reason: formatUnderscoredText(offboardingData.reasonForDeparture) || '',
        directLeave: Boolean(offboardingData.directLeave),
        resignationLetterReceived: Boolean(offboardingData.resignationLetterReceived),
        offboardingMeetingConducted: Boolean(offboardingData.offboardingMeetingConducted),
        departureAnnouncementSent: Boolean(offboardingData.departureAnnouncementSent),
        knowledgeTransfer: Boolean(offboardingData.knowledgeTransferCompleted),
        leaveBalanceSettlementSigned: Boolean(offboardingData.leaveBalanceSettlementSigned),
        companyAssetsReturned: Boolean(offboardingData.companyAssetsReturned),
        recordsUpdatedInHrSystems: Boolean(offboardingData.recordsUpdated ?? offboardingData.recordsUpdatedInHrSystems),
        communicationChannelsRemoved: Boolean(offboardingData.communicationChannelsRemoved),
        exitInterviewConducted: Boolean(offboardingData.exitInterviewConducted),
        emailOfResignationReceived: Boolean(offboardingData.emailOfResignationReceived),
        allAccessRevoked: Boolean(offboardingData.allAccessRevoked),
        alumni: Boolean(offboardingData.alumni),
        vacation: offboardingData.vacationSettlementNotes || '', notes: offboardingData.additionalNotes || '',
        annualVacationEntitlement: (offboardingData.annualVacationEntitlement || 0).toString(),
        vacationTaken: (offboardingData.vacationTaken || 0).toString(), remainingVacation: (offboardingData.remainingVacation || 0).toString(),
        createdBy: offboardingData.createdBy, createdDate: offboardingData.createdDate, lastModifiedBy: offboardingData.lastModifiedBy, lastModifiedDate: offboardingData.lastModifiedDate
      });
      setEditingId(employee.id);
      setShowForm(true);
    } catch (error) {
      setErrorMessage('Failed to load data.');
    } finally { setIsLoading(false); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this record?')) {
      try {
        await deleteOffboarding(id);
        setOffboardingRecords(prev => prev.filter(emp => emp.id !== id));
        setShowSuccess(true); setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) { setErrorMessage(error.message); }
    }
  };

  const handleAddNew = () => { resetForm(); setShowForm(true); };

  const sortRecords = (records, sortBy, sortOrder) => {
    return [...records].sort((a, b) => {
      let valA = a[sortBy], valB = b[sortBy];
      if (sortBy === 'progress') return sortOrder === 'asc' ? valA - valB : valB - valA;
      if (sortBy === 'dateOfDeparture' || sortBy === 'dateOfJoining' || sortBy.includes('Date')) {
        valA = new Date(valA || 0); valB = new Date(valB || 0);
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      }
      valA = (valA || '').toString().toLowerCase(); valB = (valB || '').toString().toLowerCase();
      return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  };

  const handleExportClick = (type) => {
    const completed = getFilteredRecords('completed');
    const inProgress = getFilteredRecords('in-progress');

    if (completed.length > 0 && inProgress.length > 0) {
      setExportDialog({ open: true, type });
    } else if (completed.length > 0) {
      if (type === 'pdf') exportToPDF('completed');
      else exportToExcel('completed');
    } else if (inProgress.length > 0) {
      if (type === 'pdf') exportToPDF('in-progress');
      else exportToExcel('in-progress');
    } else {
      alert('No records to export based on current filters.');
    }
  };

  const handleExportConfirm = (status) => {
    if (exportDialog.type === 'pdf') exportToPDF(status);
    else exportToExcel(status);
    setExportDialog({ open: false, type: '' });
  };

  const resolveOffboardingEmployeeId = (record) => {
    const existingEmployeeId = record?.offboardingData?.employeeId || record?.employeeId || '';
    const employeeName = record?.offboardingData?.employeeName || record?.employeeName || '';
    const employee = findEmployeeByIdentifier(existingEmployeeId) || findEmployeeByName(employeeName);
    return getEmployeeIdentifier(employee) || existingEmployeeId;
  };

  // Calculate working days Mon-Sat (exclude Sundays) between two dates inclusive
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    let count = 0;
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    const fin = new Date(end);
    fin.setHours(0, 0, 0, 0);
    while (cur <= fin) {
      if (cur.getDay() !== 0) count++; // 0 = Sunday
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  // Helper to get 1st day of the exit date's month
  const getStartOfExitMonth = (exitDateStr) => {
    if (!exitDateStr) return null;
    const d = new Date(exitDateStr);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), 1);
  };

  const formatDateFR = (date) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' || date instanceof Date ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) {
      const parsed = Date.parse(date);
      if (!isNaN(parsed)) return formatDateFR(new Date(parsed));
      return '';
    }
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Open preview dialog: fetch API data, merge with offboarding record, compute everything
  const openSTCPreview = async (record) => {
    const employeeId = resolveOffboardingEmployeeId(record);
    if (!employeeId) {
      alert("Erreur: ID de l'employé introuvable. Impossible de générer le document.");
      return;
    }
    setIsGeneratingStatement(true);
    try {
      // Resolve dates from offboarding record first (only to get the request parameters)
      let startDate = record?.dateOfJoining || record?.offboardingData?.dateOfJoining || null;
      let exitDate = record?.dateOfDeparture || record?.offboardingData?.dateOfDeparture || record?.exitDate || null;

      const empIdentifier = record?.employeeId || record?.offboardingData?.employeeId;
      const empName = record?.employeeName || record?.offboardingData?.employeeName;
      
      const employeeObj = findEmployeeByIdentifier(empIdentifier) || findEmployeeByName(empName);
      if (employeeObj) {
        if (!startDate) {
          startDate = employeeObj.dateOfJoining || employeeObj.joiningDate || employeeObj.startDate || null;
        }
      }

      // Format dates as YYYY-MM-DD for API calls
      const toDateStr = (d) => {
        if (!d) return null;
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return null;
        return dt.toISOString().split('T')[0];
      };
      
      // Calculate first day of the exit month to only fetch unpaid absences of the final month
      const firstDayOfExitMonth = getStartOfExitMonth(exitDate);
      const fromParam = toDateStr(firstDayOfExitMonth || startDate);
      const toParam = toDateStr(exitDate);

      // Fetch ONLY from FinalPayStatement API
      const payData = await getFinalPayStatement(employeeId, fromParam || undefined, toParam || undefined);

      // Retrieve values directly from backend FinalPayStatement payload (payData)
      const lastMonthSalary           = Number(payData.lastMonthSalary           ?? 0);
      const overTimeHours             = Number(payData.overTime                  ?? 0);
      const paidVacationDays          = Number(payData.paidVacationAllowance     ?? 0);
      const deductedVacationDays      = Number(payData.deductedVacationAllowance ?? 0);
      const deductedUnpaidAbsenceDays = Number(payData.deductedUnpaidAbsence     ?? 0);
      const noticePay                 = Number(payData.noticePay                 ?? 0);
      const severancePay              = Number(payData.severancePay              ?? 0);
      const otherBonuses              = Number(payData.otherBonuses              ?? 0);

      // Rates (Calculated strictly based on backend lastMonthSalary)
      const dailyRate = lastMonthSalary > 0 ? Number((lastMonthSalary / 26).toFixed(2)) : 0;
      const hourlyRate = dailyRate > 0 ? Number((dailyRate / 8).toFixed(2)) : 0;

      // Convert backend days/hours to MAD amounts
      const overTime                  = Number((overTimeHours * hourlyRate).toFixed(2));
      const paidVacationAllowance     = Number((paidVacationDays * 8 * hourlyRate).toFixed(2));
      const deductedVacationAllowance = Number((deductedVacationDays * 8 * hourlyRate).toFixed(2));
      const deductedUnpaidAbsence    = Number((deductedUnpaidAbsenceDays * 8 * hourlyRate).toFixed(2));

      // Resolve start / exit date — use actual employee joining date for resolvedStartDate to preserve official records
      const resolvedStartDate = startDate || payData.startDate || null;
      const resolvedExitDate = payData.endDate || exitDate || null;

      // Working days (Mon-Sat) for entire employment duration
      const workingDays = calculateWorkingDays(resolvedStartDate, resolvedExitDate);

      // Informational values for final month worked days/hours
      const finalMonthStart = getStartOfExitMonth(resolvedExitDate);
      const workedDays = finalMonthStart ? calculateWorkingDays(finalMonthStart, resolvedExitDate) : 0;
      const workedHours = workedDays * 8;

      // Map backend-only fields into existing compatible calculation variables
      const workedAmount = workedDays >= 26 ? lastMonthSalary : Number((workedHours * hourlyRate).toFixed(2));
      const extraHours = overTimeHours;
      const extraHoursPayment = overTime;
      const unpaidAbsenceDays = deductedUnpaidAbsenceDays;
      const unpaidAbsenceHours = deductedUnpaidAbsenceDays * 8;
      const unpaidDeduction = deductedUnpaidAbsence;

      const vacationTakenDays = deductedVacationDays;
      const vacationAllowedDays = paidVacationDays;
      const vacationHoursTaken = deductedVacationDays * 8;
      const allowedVacationHours = paidVacationDays * 8;
      const extraVacationHours = (deductedVacationDays - paidVacationDays) * 8;
      const vacationDeduction = deductedVacationAllowance > 0
        ? deductedVacationAllowance
        : (paidVacationAllowance > 0 ? -paidVacationAllowance : 0);

      // Calculate the sum exactly as requested using backend values
      const totalFinalPay = Number((
        workedAmount
        + overTime
        + paidVacationAllowance
        + noticePay
        + severancePay
        + otherBonuses
        - deductedVacationAllowance
        - deductedUnpaidAbsence
      ).toFixed(2));

      setStcPreview({
        employeeName: payData.employeeName || '',
        idNumber: payData.idNumber || '',
        positions: payData.positions || (payData.position ? [payData.position] : []),
        startDate: resolvedStartDate,
        exitDate: resolvedExitDate,
        workingDays,
        dailyRate,
        hourlyRate,
        lastMonthSalary,
        workedDays,
        workedHours,
        workedAmount,
        vacationTakenDays,
        vacationHoursTaken,
        vacationAllowedDays,
        allowedVacationHours,
        extraVacationHours,
        vacationDeduction,
        unpaidAbsenceDays,
        unpaidAbsenceHours,
        unpaidDeduction,
        extraHours,
        extraHoursPayment,
        noticePay,
        severancePay,
        otherBonuses,
        totalFinalPay,
      });
      setStcTab('form');
      setStcDialog({ open: true, record, showPreview: true });
    } catch (error) {
      console.error('STC Preview Error:', error);
      alert('Erreur lors de la récupération des données.');
    } finally {
      setIsGeneratingStatement(false);
    }
  };

  const handleStcValueChange = (field, val) => {
    if (!stcPreview) return;
    const numVal = parseFloat(val) || 0;
    setStcPreview(prev => {
      const updated = {
        ...prev,
        [field]: numVal
      };

      // Recompute rates
      updated.dailyRate = updated.lastMonthSalary > 0 ? Number((updated.lastMonthSalary / 26).toFixed(2)) : 0;
      updated.hourlyRate = updated.dailyRate > 0 ? Number((updated.dailyRate / 8).toFixed(2)) : 0;

      // Recompute workedAmount
      updated.workedAmount = updated.workedDays >= 26 ? updated.lastMonthSalary : Number((updated.workedHours * updated.hourlyRate).toFixed(2));

      // Recompute overtime payment
      updated.extraHoursPayment = Number((updated.extraHours * updated.hourlyRate).toFixed(2));

      // Recompute vacation
      updated.vacationHoursTaken = updated.vacationTakenDays * 8;
      updated.allowedVacationHours = updated.vacationAllowedDays * 8;
      updated.extraVacationHours = (updated.vacationTakenDays - updated.vacationAllowedDays) * 8;

      const paidVacationAllowance = Number((updated.vacationAllowedDays * 8 * updated.hourlyRate).toFixed(2));
      const deductedVacationAllowance = Number((updated.vacationTakenDays * 8 * updated.hourlyRate).toFixed(2));
      updated.vacationDeduction = deductedVacationAllowance > 0
        ? deductedVacationAllowance
        : (paidVacationAllowance > 0 ? -paidVacationAllowance : 0);

      // Recompute unpaid deduction
      updated.unpaidAbsenceHours = updated.unpaidAbsenceDays * 8;
      updated.unpaidDeduction = Number((updated.unpaidAbsenceHours * updated.hourlyRate).toFixed(2));

      // Recompute totalFinalPay using exact backend logic
      const overTimeMAD = updated.extraHoursPayment;
      const paidVacMAD = updated.vacationDeduction < 0 ? Math.abs(updated.vacationDeduction) : 0;
      const deductedVacMAD = updated.vacationDeduction > 0 ? updated.vacationDeduction : 0;
      const unpaidMAD = updated.unpaidDeduction;

      updated.totalFinalPay = Number((
        updated.workedAmount
        + overTimeMAD
        + paidVacMAD
        + updated.noticePay
        + updated.severancePay
        + updated.otherBonuses
        - deductedVacMAD
        - unpaidMAD
      ).toFixed(2));

      return updated;
    });
  };

  const generateSTCPDF = async (preview) => {
    if (!preview) return;
    const {
      employeeName, idNumber, positions, startDate, exitDate,
      dailyRate, hourlyRate, lastMonthSalary,
      workedDays, workedHours, workedAmount,
      vacationTakenDays, vacationHoursTaken,
      vacationAllowedDays, allowedVacationHours,
      extraVacationHours, vacationDeduction,
      unpaidAbsenceDays, unpaidAbsenceHours, unpaidDeduction,
      extraHours, extraHoursPayment,
      noticePay, severancePay, otherBonuses,
      totalFinalPay
    } = preview;

    setIsGeneratingStatement(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;

      // ==========================================
      // PAGE 1: OFFICIAL FRENCH STC FORM
      // ==========================================

      // Title
      doc.setFontSize(22);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text('Solde de Tout Compte', pageWidth / 2, 25, { align: 'center' });

      // Metadata
      doc.setFontSize(11);
      let metaY = 42;
      const printMetaLine = (label, val) => {
        doc.setFont('helvetica', 'normal');
        doc.text(`${label} : `, margin, metaY);
        doc.setFont('helvetica', 'bold');
        doc.text(String(val ?? 'N/A'), margin + doc.getTextWidth(`${label} : `), metaY);
        metaY += 8;
      };

      printMetaLine('Nom et prénom', employeeName);
      printMetaLine('CIN', idNumber || 'N/A');
      printMetaLine('Poste occupé', positions?.join(', ') || 'N/A');
      printMetaLine('Date d’entrée', formatDateFR(startDate) || 'N/A');
      printMetaLine('Date de sortie', formatDateFR(exitDate) || 'N/A');

      metaY += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      doc.text('Détails du solde de tout compte', margin, metaY);
      doc.line(margin, metaY + 2, pageWidth - margin, metaY + 2);
      metaY += 10;
 
      doc.setFontSize(11);
      const printDetailLine = (label, numVal, unit = 'DH') => {
        doc.setFont('helvetica', 'normal');
        doc.text(`${label} :`, margin, metaY);
        
        // Print unit normally on the right margin
        doc.setFont('helvetica', 'normal');
        const unitText = ` ${unit}`;
        const unitWidth = doc.getTextWidth(unitText);
        doc.text(unitText, pageWidth - margin - unitWidth, metaY);
        
        // Print number in bold to the left of the unit
        doc.setFont('helvetica', 'bold');
        const numStr = String(numVal);
        doc.text(numStr, pageWidth - margin - unitWidth - doc.getTextWidth(numStr), metaY);
        
        metaY += 8;
      };
 
      printDetailLine('Salaire du dernier mois', workedAmount.toFixed(2));
      printDetailLine('Heures supplémentaires', extraHoursPayment.toFixed(2));
      printDetailLine('Indemnité de congés payés', vacationDeduction < 0 ? Math.abs(vacationDeduction).toFixed(2) : '0.00');
      printDetailLine('Retenue pour congés pris en excès', vacationDeduction > 0 ? vacationDeduction.toFixed(2) : '0.00');
      printDetailLine('Retenue absences non payées', unpaidDeduction.toFixed(2));
      printDetailLine('Indemnité de préavis', noticePay.toFixed(2));
      printDetailLine('Indemnité de licenciement', severancePay.toFixed(2));
      printDetailLine('Autres primes', otherBonuses.toFixed(2));

      metaY += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      doc.text('Montant total dû', margin, metaY);
      doc.line(margin, metaY + 2, pageWidth - margin, metaY + 2);
      metaY += 8;

      // Net total banner box
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      
      const isPositive = totalFinalPay >= 0;
      const line1 = isPositive
        ? "Montant total net dû à l’employé après calcul de tous les éléments de rémunération"
        : "Montant total net à rembourser par l’employé après calcul de tous les éléments de";
      const line2Prefix = isPositive
        ? "et déduction des retenues applicables : "
        : "rémunération et déduction des retenues applicables : ";
      
      const boxHeight = 18;
      doc.rect(margin, metaY, pageWidth - margin * 2, boxHeight, 'FD');
 
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      
      // Print Line 1 in normal
      doc.setFont('helvetica', 'normal');
      doc.text(line1, margin + 5, metaY + 6);
      
      // Print Line 2 Prefix in normal
      doc.text(line2Prefix, margin + 5, metaY + 12);
      
      let curBannerX = margin + 5 + doc.getTextWidth(line2Prefix);
      
      // Print operator normally
      doc.setFont('helvetica', 'normal');
      const signChar = isPositive ? '+' : '−';
      doc.text(signChar, curBannerX, metaY + 12);
      curBannerX += doc.getTextWidth(signChar);
      
      // Print number in bold
      doc.setFont('helvetica', 'bold');
      const valStr = Math.abs(totalFinalPay).toFixed(2);
      doc.text(valStr, curBannerX, metaY + 12);
      curBannerX += doc.getTextWidth(valStr);
      
      // Print unit normally
      doc.setFont('helvetica', 'normal');
      doc.text(' DH', curBannerX, metaY + 12);

      metaY += boxHeight + 8;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text('Sauf erreur, omission ou opérations en cours.', margin, metaY);
      metaY += 10;

      // Attestation of receipt
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text('Attestation de réception', margin, metaY);
      metaY += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      
      const p1 = "Je soussigné ";
      const p2 = employeeName;
      const p3 = " reconnais avoir reçu de la part de MARABES GROUP le montant susmentionné ";
      const p4 = "en règlement de mon solde de tout compte.";

      doc.setFont('helvetica', 'normal');
      doc.text(p1, margin, metaY);
      
      doc.setFont('helvetica', 'bold');
      doc.text(p2, margin + doc.getTextWidth(p1), metaY);
      
      doc.setFont('helvetica', 'normal');
      doc.text(p3, margin + doc.getTextWidth(p1) + doc.getTextWidth(p2), metaY);
      
      doc.text(p4, margin, metaY + 5.5);
      metaY += 11;

      metaY += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text("Fait à Nador, le ", margin, metaY);
      doc.setFont('helvetica', 'bold');
      doc.text(formatDateFR(new Date()), margin + doc.getTextWidth("Fait à Nador, le "), metaY);
      
      doc.setFont('helvetica', 'normal');
      doc.text('Signature de l’employé :', pageWidth - margin - 5, metaY, { align: 'right' });


      // ==========================================
      // PAGE 2: DETAILED FORMULA BREAKDOWN
      // ==========================================
      doc.addPage();

      // Title
      doc.setFontSize(18);
      const primaryColor = [40, 58, 139]; // Navy
      const darkColor = [30, 41, 59]; // Slate
      const lightBg = [248, 250, 252]; // Slate 50
      const borderStroke = [226, 232, 240]; // Slate 200

      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFont('helvetica', 'normal');
      doc.text('Détails des Calculs (Étape par Étape)', pageWidth / 2, 20, { align: 'center' });

      // Two-column layout starting at Y=30
      let leftY = 30;
      let rightY = 30;
      const colW = 82;
      const leftX = margin;
      const rightX = margin + colW + 6;

      const drawStepCard = (x, y, title, formula, calcSegments) => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(borderStroke[0], borderStroke[1], borderStroke[2]);
        doc.roundedRect(x, y, colW, 17, 2, 2, 'FD');

        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(x, y, 1.5, 17, 'F');

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(title, x + 4, y + 4.5);

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`Formule: ${formula}`, x + 4, y + 8.5);

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        doc.text('Calcul: ', x + 4, y + 13.5);

        let curX = x + 4 + doc.getTextWidth('Calcul: ');
        calcSegments.forEach(seg => {
          doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
          doc.text(seg.text, curX, y + 13.5);
          curX += doc.getTextWidth(seg.text);
        });

        return y + 21;
      };

      // Left Column steps
      leftY = drawStepCard(leftX, leftY, '1. Worked Hours', 'Worked Days × 8', [
        { text: String(workedDays), bold: true },
        { text: ' \u00d7 8 = ', bold: false },
        { text: String(workedHours), bold: true },
        { text: ' hours', bold: false }
      ]);
      leftY = drawStepCard(leftX, leftY, '2. Vacation Hours Taken', 'Vacation Days Taken × 8', [
        { text: String(vacationTakenDays), bold: true },
        { text: ' \u00d7 8 = ', bold: false },
        { text: String(vacationHoursTaken), bold: true },
        { text: ' hours', bold: false }
      ]);
      leftY = drawStepCard(leftX, leftY, '3. Allowed Vacation Hours', 'Allowed Vacation Days × 8', [
        { text: String(vacationAllowedDays), bold: true },
        { text: ' \u00d7 8 = ', bold: false },
        { text: String(allowedVacationHours), bold: true },
        { text: ' hours', bold: false }
      ]);
      leftY = drawStepCard(leftX, leftY, '4. Extra Vacation Hours', '(Vacation Taken Days − Allowed Vacation Days) × 8', [
        { text: '(', bold: false },
        { text: String(vacationTakenDays), bold: true },
        { text: ' \u2212 ', bold: false },
        { text: String(vacationAllowedDays), bold: true },
        { text: ') \u00d7 8 = ', bold: false },
        { text: String(extraVacationHours), bold: true },
        { text: ' hours', bold: false }
      ]);
      leftY = drawStepCard(leftX, leftY, '5. Daily Rate', 'Monthly Salary ÷ Worked Days', [
        { text: lastMonthSalary.toFixed(2), bold: true },
        { text: ' \u00f7 26 = ', bold: false },
        { text: dailyRate.toFixed(2), bold: true },
        { text: ' MAD', bold: false }
      ]);

      // Right Column steps
      rightY = drawStepCard(rightX, rightY, '6. Hourly Rate', 'Daily Rate ÷ 8', [
        { text: dailyRate.toFixed(2), bold: true },
        { text: ' \u00f7 8 = ', bold: false },
        { text: hourlyRate.toFixed(2), bold: true },
        { text: ' MAD', bold: false }
      ]);
      if (workedDays >= 26) {
        rightY = drawStepCard(rightX, rightY, '7. Worked Amount (Salaire du dernier mois)', 'Contractual Monthly Salary', [
          { text: lastMonthSalary.toFixed(2), bold: true },
          { text: ' = ', bold: false },
          { text: workedAmount.toFixed(2), bold: true },
          { text: ' MAD', bold: false }
        ]);
      } else {
        rightY = drawStepCard(rightX, rightY, '7. Worked Amount (Salaire du dernier mois)', 'Worked Hours × Hourly Rate', [
          { text: String(workedHours), bold: true },
          { text: ' \u00d7 ', bold: false },
          { text: hourlyRate.toFixed(2), bold: true },
          { text: ' = ', bold: false },
          { text: workedAmount.toFixed(2), bold: true },
          { text: ' MAD', bold: false }
        ]);
      }
      rightY = drawStepCard(rightX, rightY, '8. Vacation Deduction', 'Extra Vacation Hours × Hourly Rate', [
        { text: String(extraVacationHours), bold: true },
        { text: ' \u00d7 ', bold: false },
        { text: hourlyRate.toFixed(2), bold: true },
        { text: ' = ', bold: false },
        { text: vacationDeduction.toFixed(2), bold: true },
        { text: ' MAD', bold: false }
      ]);
      rightY = drawStepCard(rightX, rightY, '9. Unpaid Deduction', 'Unpaid Absence Hours × Hourly Rate', [
        { text: String(unpaidAbsenceHours), bold: true },
        { text: ' \u00d7 ', bold: false },
        { text: hourlyRate.toFixed(2), bold: true },
        { text: ' = ', bold: false },
        { text: unpaidDeduction.toFixed(2), bold: true },
        { text: ' MAD', bold: false }
      ]);
      rightY = drawStepCard(rightX, rightY, '10. Extra Hours Payment', 'Extra Hours × Hourly Rate', [
        { text: String(extraHours), bold: true },
        { text: ' \u00d7 ', bold: false },
        { text: hourlyRate.toFixed(2), bold: true },
        { text: ' = ', bold: false },
        { text: extraHoursPayment.toFixed(2), bold: true },
        { text: ' MAD', bold: false }
      ]);

      let maxY = Math.max(leftY, rightY);
      const stcY = maxY - 2;
      const hasIndemnities = (noticePay > 0 || severancePay > 0 || otherBonuses > 0);
      const stcH = hasIndemnities ? 45 : 36;

      doc.setFillColor(lightBg[0], lightBg[1], lightBg[2]);
      doc.setDrawColor(borderStroke[0], borderStroke[1], borderStroke[2]);
      doc.roundedRect(margin, stcY, 170, stcH, 3, 3, 'FD');

      doc.setFillColor(totalFinalPay >= 0 ? 22 : 220, totalFinalPay >= 0 ? 163 : 38, totalFinalPay >= 0 ? 74 : 38);
      doc.rect(margin, stcY, 2, stcH, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('11. Final Settlement (Règlement final)', margin + 6, stcY + 6);

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text('Formule: Worked Amount − Vacation Deduction − Unpaid Deduction + Extra Hours Payment + Indemnités', margin + 6, stcY + 10.5);

      doc.setFontSize(8.5);
      doc.setTextColor(darkColor[0], darkColor[1], darkColor[2]);

      const mathLine1Segments = [
        { text: '  ', bold: false },
        { text: `${workedAmount.toFixed(2)}`, bold: true },
        { text: ' (Worked Amount) ', bold: false },
        { text: vacationDeduction >= 0 ? '− ' : '+ ', bold: false },
        { text: `${Math.abs(vacationDeduction).toFixed(2)}`, bold: true },
        { text: ' (Vacation Deduction)', bold: false }
      ];

      const mathLine2Segments = [
        { text: '  − ', bold: false },
        { text: `${unpaidDeduction.toFixed(2)}`, bold: true },
        { text: ' (Unpaid Deduction) + ', bold: false },
        { text: `${extraHoursPayment.toFixed(2)}`, bold: true },
        { text: ' (Extra Hours Payment)', bold: false }
      ];
      if (noticePay > 0) {
        mathLine2Segments.push(
          { text: ' + ', bold: false },
          { text: `${noticePay.toFixed(2)}`, bold: true },
          { text: ' (Notice Pay)', bold: false }
        );
      }
      if (severancePay > 0) {
        mathLine2Segments.push(
          { text: ' + ', bold: false },
          { text: `${severancePay.toFixed(2)}`, bold: true },
          { text: ' (Severance Pay)', bold: false }
        );
      }
      if (otherBonuses > 0) {
        mathLine2Segments.push(
          { text: ' + ', bold: false },
          { text: `${otherBonuses.toFixed(2)}`, bold: true },
          { text: ' (Other Bonuses)', bold: false }
        );
      }

      const printMixedLine = (x, y, segments) => {
        let currentX = x;
        segments.forEach(seg => {
          doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
          doc.text(seg.text, currentX, y);
          currentX += doc.getTextWidth(seg.text);
        });
      };

      printMixedLine(margin + 6, stcY + 16.5, mathLine1Segments);
      printMixedLine(margin + 6, stcY + 22.5, mathLine2Segments);

      doc.setFontSize(12);
      doc.setTextColor(totalFinalPay >= 0 ? 22 : 220, totalFinalPay >= 0 ? 163 : 38, totalFinalPay >= 0 ? 74 : 38);
      
      let finalMathX = margin + 6;
      
      // Print = normally
      doc.setFont('helvetica', 'normal');
      doc.text('= ', finalMathX, stcY + stcH - 6);
      finalMathX += doc.getTextWidth('= ');
      
      // Print sign normally
      const finalSign = totalFinalPay >= 0 ? '+' : '−';
      doc.text(finalSign, finalMathX, stcY + stcH - 6);
      finalMathX += doc.getTextWidth(finalSign);
      
      // Print number in bold
      doc.setFont('helvetica', 'bold');
      const finalNum = Math.abs(totalFinalPay).toFixed(2);
      doc.text(finalNum, finalMathX, stcY + stcH - 6);
      finalMathX += doc.getTextWidth(finalNum);
      
      // Print MAD normally
      doc.setFont('helvetica', 'normal');
      doc.text(' MAD', finalMathX, stcY + stcH - 6);

      doc.save(`Solde_de_Tout_Compte_${employeeName.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error('STC PDF Error:', error);
      alert('Erreur lors de la génération du PDF.');
    } finally {
      setIsGeneratingStatement(false);
    }
  };

  const generateSTCWord = async (preview) => {
    if (!preview) return;
    const {
      employeeName, idNumber, positions, startDate, exitDate,
      dailyRate, hourlyRate, lastMonthSalary,
      workedDays, workedHours, workedAmount,
      vacationTakenDays, vacationHoursTaken,
      vacationAllowedDays, allowedVacationHours,
      extraVacationHours, vacationDeduction,
      unpaidAbsenceDays, unpaidAbsenceHours, unpaidDeduction,
      extraHours, extraHoursPayment,
      noticePay, severancePay, otherBonuses,
      totalFinalPay
    } = preview;

    setIsGeneratingStatement(true);
    try {
      const boldText = (text, size = 22, color = '000000') => new TextRun({ text: String(text ?? ''), bold: true, size, color });
      const normalText = (text, size = 22) => new TextRun({ text: String(text ?? ''), size });
      const italicText = (text, size = 20, color = '64748B') => new TextRun({ text: String(text ?? ''), italic: true, size, color });

      const createStepBlock = (title, formula, calcSegments) => [
        new Paragraph({
          children: [normalText(title, 24)],
          spacing: { before: 180, after: 60 }
        }),
        new Paragraph({
          children: [normalText('Formule : ', 22), italicText(formula, 22)],
          spacing: { after: 60 }
        }),
        new Paragraph({
          children: [
            normalText('Calcul : ', 22),
            ...calcSegments.map(seg => seg.bold ? boldText(seg.text, 22, '1E293B') : normalText(seg.text, 22))
          ],
          spacing: { after: 120 }
        })
      ];

      const children = [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun({ text: 'Solde de Tout Compte', bold: false, size: 36, color: '000000' })],
          spacing: { after: 400 }
        }),

        new Paragraph({
          children: [normalText('Nom et prénom : ', 22), boldText(employeeName, 22, '000000')],
          spacing: { after: 120 }
        }),
        new Paragraph({
          children: [normalText('CIN : ', 22), boldText(idNumber || 'N/A', 22, '000000')],
          spacing: { after: 120 }
        }),
        new Paragraph({
          children: [normalText('Poste occupé : ', 22), boldText(positions?.join(', ') || 'N/A', 22, '000000')],
          spacing: { after: 120 }
        }),
        new Paragraph({
          children: [normalText('Date d’entrée : ', 22), boldText(formatDateFR(startDate) || 'N/A', 22, '000000')],
          spacing: { after: 120 }
        }),
        new Paragraph({
          children: [normalText('Date de sortie : ', 22), boldText(formatDateFR(exitDate) || 'N/A', 22, '000000')],
          spacing: { after: 400 }
        }),

        new Paragraph({
          children: [normalText('Détails du solde de tout compte', 24)],
          spacing: { before: 200, after: 200 }
        }),

        new Paragraph({
          children: [normalText('Salaire du dernier mois : ', 22), boldText(workedAmount.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [normalText('Heures supplémentaires : ', 22), boldText(extraHoursPayment.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [
            normalText('Indemnité de congés payés : ', 22), 
            boldText(vacationDeduction < 0 ? Math.abs(vacationDeduction).toFixed(2) : '0.00', 22, '000000'), 
            normalText(' DH', 22)
          ],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [
            normalText('Retenue pour congés pris en excès : ', 22), 
            boldText(vacationDeduction > 0 ? vacationDeduction.toFixed(2) : '0.00', 22, '000000'), 
            normalText(' DH', 22)
          ],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [normalText('Retenue absences non payées : ', 22), boldText(unpaidDeduction.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [normalText('Indemnité de préavis : ', 22), boldText(noticePay.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [normalText('Indemnité de licenciement : ', 22), boldText(severancePay.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [normalText('Autres primes : ', 22), boldText(otherBonuses.toFixed(2), 22, '000000'), normalText(' DH', 22)],
          spacing: { after: 300 }
        }),

        new Paragraph({
          children: [normalText('Montant total dû', 24)],
          spacing: { before: 200, after: 100 }
        }),

        new Paragraph({
          children: [
            totalFinalPay >= 0 
              ? normalText('Montant total net dû à l’employé après calcul de tous les éléments de rémunération et déduction des retenues applicables : ', 22)
              : normalText('Montant total net à rembourser par l’employé après calcul de tous les éléments de rémunération et déduction des retenues applicables : ', 22),
            totalFinalPay >= 0 ? normalText('+', 22) : normalText('−', 22),
            boldText(Math.abs(totalFinalPay).toFixed(2), 22, '000000'),
            normalText(' DH', 22)
          ],
          spacing: { after: 300 }
        }),

        new Paragraph({
          children: [italicText('Sauf erreur, omission ou opérations en cours.', 20)],
          spacing: { after: 400 }
        }),

        new Paragraph({
          children: [normalText('Attestation de réception', 24)],
          spacing: { after: 150 }
        }),

        new Paragraph({
          children: [
            normalText('Je soussigné '),
            boldText(employeeName, 22, '000000'),
            normalText(' reconnais avoir reçu de la part de '),
            normalText('MARABES GROUP', 22),
            normalText(' le montant susmentionné en règlement de mon solde de tout compte.')
          ],
          spacing: { after: 400 }
        }),

        new Paragraph({
          children: [
            normalText('Fait à Nador, le '),
            boldText(formatDateFR(new Date()), 22, '000000'),
            normalText(' '.repeat(40)),
            normalText('Signature de l’employé :')
          ]
        }),

        // Page break to step-by-step calculations
        new Paragraph({
          children: [new PageBreak()]
        }),

        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [normalText('Détails des Calculs (Étape par Étape)', 28)],
          spacing: { before: 200, after: 200 }
        }),

        // 1. Worked Hours
        ...createStepBlock(
          '1. Worked Hours',
          'Worked Days × 8',
          [
            { text: String(workedDays), bold: true },
            { text: ' × 8 = ', bold: false },
            { text: String(workedHours), bold: true },
            { text: ' hours', bold: false }
          ]
        ),

        // 2. Vacation Hours Taken
        ...createStepBlock(
          '2. Vacation Hours Taken',
          'Vacation Days Taken × 8',
          [
            { text: String(vacationTakenDays), bold: true },
            { text: ' × 8 = ', bold: false },
            { text: String(vacationHoursTaken), bold: true },
            { text: ' hours', bold: false }
          ]
        ),

        // 3. Allowed Vacation Hours
        ...createStepBlock(
          '3. Allowed Vacation Hours',
          'Allowed Vacation Days × 8',
          [
            { text: String(vacationAllowedDays), bold: true },
            { text: ' × 8 = ', bold: false },
            { text: String(allowedVacationHours), bold: true },
            { text: ' hours', bold: false }
          ]
        ),

        // 4. Extra Vacation Hours
        ...createStepBlock(
          '4. Extra Vacation Hours',
          '(Vacation Taken Days − Allowed Vacation Days) × 8',
          [
            { text: '(', bold: false },
            { text: String(vacationTakenDays), bold: true },
            { text: ' − ', bold: false },
            { text: String(vacationAllowedDays), bold: true },
            { text: ') × 8 = ', bold: false },
            { text: String(extraVacationHours), bold: true },
            { text: ' hours', bold: false }
          ]
        ),

        // 5. Daily Rate
        ...createStepBlock(
          '5. Daily Rate',
          'Monthly Salary ÷ Worked Days',
          [
            { text: lastMonthSalary.toFixed(2), bold: true },
            { text: ' ÷ 26 = ', bold: false },
            { text: dailyRate.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ),

        // 6. Hourly Rate
        ...createStepBlock(
          '6. Hourly Rate',
          'Daily Rate ÷ 8',
          [
            { text: dailyRate.toFixed(2), bold: true },
            { text: ' ÷ 8 = ', bold: false },
            { text: hourlyRate.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ),

        // 7. Worked Amount (Salaire du dernier mois)
        ...(workedDays >= 26 ? createStepBlock(
          '7. Worked Amount (Salaire du dernier mois)',
          'Contractual Monthly Salary',
          [
            { text: lastMonthSalary.toFixed(2), bold: true },
            { text: ' = ', bold: false },
            { text: workedAmount.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ) : createStepBlock(
          '7. Worked Amount (Salaire du dernier mois)',
          'Worked Hours × Hourly Rate',
          [
            { text: String(workedHours), bold: true },
            { text: ' × ', bold: false },
            { text: hourlyRate.toFixed(2), bold: true },
            { text: ' = ', bold: false },
            { text: workedAmount.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        )),

        // 8. Vacation Deduction
        ...createStepBlock(
          '8. Vacation Deduction',
          'Extra Vacation Hours × Hourly Rate',
          [
            { text: String(extraVacationHours), bold: true },
            { text: ' × ', bold: false },
            { text: hourlyRate.toFixed(2), bold: true },
            { text: ' = ', bold: false },
            { text: vacationDeduction.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ),

        // 9. Unpaid Deduction
        ...createStepBlock(
          '9. Unpaid Deduction',
          'Unpaid Absence Hours × Hourly Rate',
          [
            { text: String(unpaidAbsenceHours), bold: true },
            { text: ' × ', bold: false },
            { text: hourlyRate.toFixed(2), bold: true },
            { text: ' = ', bold: false },
            { text: unpaidDeduction.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ),

        // 10. Extra Hours Payment
        ...createStepBlock(
          '10. Extra Hours Payment',
          'Extra Hours × Hourly Rate',
          [
            { text: String(extraHours), bold: true },
            { text: ' × ', bold: false },
            { text: hourlyRate.toFixed(2), bold: true },
            { text: ' = ', bold: false },
            { text: extraHoursPayment.toFixed(2), bold: true },
            { text: ' MAD', bold: false }
          ]
        ),

        new Paragraph({
          children: [normalText('11. Final Settlement (Règlement final)', 26)],
          spacing: { before: 240, after: 120 }
        }),
        new Paragraph({
          children: [normalText('Formule : ', 22), italicText('Worked Amount − Vacation Deduction − Unpaid Deduction + Extra Hours Payment + Indemnités', 22)],
          spacing: { after: 60 }
        }),
        new Paragraph({
          children: [normalText('Calcul détaillé : ', 22)],
          spacing: { after: 60 }
        }),
        ...[
          { label: 'Worked Amount : ', num: workedAmount.toFixed(2) },
          vacationDeduction >= 0 
            ? { label: '− Vacation Deduction : ', num: vacationDeduction.toFixed(2) }
            : { label: '+ Vacation Payout : ', num: Math.abs(vacationDeduction).toFixed(2) },
          { label: '− Unpaid Deduction : ', num: unpaidDeduction.toFixed(2) },
          { label: '+ Extra Hours Payment : ', num: extraHoursPayment.toFixed(2) },
          noticePay > 0 ? { label: '+ Notice Pay : ', num: noticePay.toFixed(2) } : null,
          severancePay > 0 ? { label: '+ Severance Pay : ', num: severancePay.toFixed(2) } : null,
          otherBonuses > 0 ? { label: '+ Other Bonuses : ', num: otherBonuses.toFixed(2) } : null,
        ].filter(Boolean).map(step => new Paragraph({
          children: [
            normalText('   ', 22),
            normalText(step.label, 22),
            boldText(step.num, 22, '1E293B'),
            normalText(' MAD', 22)
          ],
          spacing: { after: 40 }
        })),
        new Paragraph({
          children: [
            normalText('   = ', 24),
            normalText(totalFinalPay >= 0 ? '+' : '−', 24, totalFinalPay >= 0 ? '16A34A' : 'DC2626'),
            boldText(Math.abs(totalFinalPay).toFixed(2), 24, totalFinalPay >= 0 ? '16A34A' : 'DC2626'),
            normalText(' MAD', 24, totalFinalPay >= 0 ? '16A34A' : 'DC2626')
          ],
          spacing: { before: 100, after: 180 }
        })
      ];

      const doc = new Document({
        sections: [{
          properties: {},
          children
        }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Solde_de_Tout_Compte_${employeeName.replace(/\s+/g, '_')}.docx`);
    } catch (error) {
      console.error('STC Word Error:', error);
      alert('Erreur lors de la génération du document Word.');
    } finally {
      setIsGeneratingStatement(false);
    }
  };

  const exportToPDF = async (status) => {
    setIsExportingPdf(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const margin = 5;
      const contentWidth = pageWidth - (margin * 2);

      const axoLogoUrl = '/cLogo.png';
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);

      try { doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12); } catch (e) { console.warn("Marabes logo failed", e); }
      if (axoBase64) { doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12); }

      doc.setDrawColor(63, 81, 181);
      doc.setLineWidth(0.5);
      doc.line(margin, 25, pageWidth - margin, 25);

      doc.setFillColor(232, 245, 255);
      doc.rect(margin, 30, contentWidth, 14, 'F');

      const textY = 39;
      doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, margin + 3, textY);

      doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 58, 139);
      const titleStatus = status === 'all' ? 'All' : (status === 'in-progress' ? 'In Progress' : 'Completed');
      const title = `Offboarding Records - ${titleStatus}`;
      doc.text(title, pageWidth / 2, textY, { align: 'center' });

      const recordsToExport = status === 'all'
        ? [...getFilteredRecords('completed'), ...getFilteredRecords('in-progress')]
        : getFilteredRecords(status);

      doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
      const recordsText = `Total Records: ${recordsToExport.length}`;
      const textWidth = doc.getTextWidth(recordsText);
      doc.text(recordsText, margin + contentWidth - 3 - textWidth, textY);

      const headRows = [
        [
          { content: 'Personal Information', colSpan: 5, styles: { halign: 'center', fillColor: [241, 245, 249], textColor: [30, 41, 59] } },
          { content: 'Mandatory Checklist', colSpan: 10, styles: { halign: 'center', fillColor: [220, 252, 231], textColor: [22, 101, 52] } },
          { content: 'Optional', colSpan: 1, styles: { halign: 'center', fillColor: [255, 237, 213], textColor: [154, 52, 18] } },
        ],
        [
          { content: 'Name', styles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Department', styles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Office', styles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Start Date', styles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Exit Date', styles: { fillColor: [248, 250, 252], textColor: [71, 85, 105], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Exit Interview', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Resignation Email', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'HR Records Updated', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'All Access Revoked', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Comms Removed', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Resignation Letter', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Announcement', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Leave Balance', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Offboarding Mtg', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Assets Returned', styles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'normal', fontSize: 5 } },
          { content: 'Knowledge Transfer', styles: { fillColor: [255, 247, 237], textColor: [194, 65, 12], fontStyle: 'normal', fontSize: 5 } },
        ]
      ];

      const tableBody = recordsToExport.map(emp => {
        const check = (val) => val ? 'Y' : 'N';
        return [
          emp.employeeName,
          emp.department,
          emp.office,
          formatDateDDMMYYYY(emp.dateOfJoining),
          formatDateDDMMYYYY(emp.dateOfDeparture),
          check(emp.exitInterviewConducted),
          check(emp.emailOfResignationReceived),
          check(emp.recordsUpdatedInHrSystems),
          check(emp.allAccessRevoked),
          check(emp.communicationChannelsRemoved),
          check(emp.resignationLetterReceived),
          check(emp.departureAnnouncementSent),
          check(emp.leaveBalanceSettlementSigned),
          check(emp.offboardingMeetingConducted),
          check(emp.companyAssetsReturned),
          check(emp.knowledgeTransfer),
        ];
      });

      autoTable(doc, {
        startY: 45,
        head: headRows,
        body: tableBody,
        theme: 'grid',
        tableWidth: contentWidth,
        headStyles: { textColor: 255, fontSize: 6.5, fontStyle: 'bold', halign: 'center', valign: 'middle' },
        styles: { fontSize: 6.5, cellPadding: 1.2, valign: 'middle', halign: 'center', overflow: 'linebreak', lineColor: [226, 232, 240], lineWidth: 0.1 },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'bold' }
        },
        margin: { left: margin, right: margin },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index >= 5) {
            data.cell.text = [''];
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index >= 5) {
            const isChecked = data.cell.raw === 'Y';
            const cx = data.cell.x + data.cell.width / 2;
            const cy = data.cell.y + data.cell.height / 2;
            const r = 2;

            if (isChecked) {
              doc.setFillColor(34, 197, 94);
              doc.roundedRect(cx - r, cy - r, r * 2, r * 2, 0.5, 0.5, 'F');
              doc.setDrawColor(255, 255, 255);
              doc.setLineWidth(0.6);
              doc.line(cx - 1.2, cy + 0.2, cx - 0.2, cy + 1.2);
              doc.line(cx - 0.2, cy + 1.2, cx + 1.5, cy - 1);
            } else {
              doc.setDrawColor(200, 200, 200);
              doc.setLineWidth(0.4);
              doc.setFillColor(255, 255, 255);
              doc.roundedRect(cx - r, cy - r, r * 2, r * 2, 0.5, 0.5, 'FD');
            }
          }
        }
      });

      doc.save(`offboarding_${titleStatus.replace(' ', '_').toLowerCase()}_records.pdf`);
    } catch (error) {
      console.error('PDF Export Error:', error);
      alert('Failed to generate PDF');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const exportToExcel = (status) => {
    try {
      const columns = [
        { header: 'Name', key: 'employeeName' },
        { header: 'Department', key: 'department' },
        { header: 'Office', key: 'office' },
        { header: 'Start Date', key: 'startDate' },
        { header: 'Exit Date', key: 'exitDate' },
        { header: 'Reason', key: 'reason' },
        { header: 'Description', key: 'notes' },
        { header: 'Progress', key: 'progress' },
        { header: 'Status', key: 'status' }
      ];
      const recordsToExport = status === 'all'
        ? [...getFilteredRecords('completed'), ...getFilteredRecords('in-progress')]
        : getFilteredRecords(status);
      const worksheetData = recordsToExport.map(emp => ({
        employeeName: emp.employeeName,
        department: emp.department,
        office: emp.office,
        startDate: formatDateDDMMYYYY(emp.dateOfJoining),
        exitDate: formatDateDDMMYYYY(emp.dateOfDeparture),
        reason: emp.reason,
        notes: emp.notes,
        progress: `${emp.progress}%`,
        status: emp.status === 'completed' ? 'Completed' : 'In Progress'
      }));
      const ws = XLSX.utils.json_to_sheet(worksheetData, { header: columns.map(c => c.key) });
      XLSX.utils.sheet_add_aoa(ws, [columns.map(c => c.header)], { origin: "A1" });
      const colWidths = columns.map(col => ({ wch: Math.max(col.header.length, ...worksheetData.map(r => (r[col.key]?.toString().length || 10))) }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      const titleStatus = status === 'all' ? 'All' : (status === 'in-progress' ? 'In_Progress' : 'Completed');
      XLSX.utils.book_append_sheet(wb, ws, `Offboarding_${titleStatus}`);
      XLSX.writeFile(wb, `offboarding_${titleStatus.toLowerCase()}_records.xlsx`);
    } catch (error) {
      console.error('Excel Export Error:', error);
      alert('Failed to generate Excel file');
    }
  };

  const exportInProgressOffboardingPDF = async () => {
    const records = getFilteredRecords('in-progress');
    if (records.length === 0) {
      alert('No in-progress offboardings to export.');
      setIsExportingInProgressPdf(false);
      return;
    }
    setIsExportingInProgressPdf(true);
    try {
      const imageDataMap = {};
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const empId = resolveOffboardingEmployeeId(record);
        if (empId) {
          const imageUrl = await getEmployeeImage(empId);
          if (imageUrl) {
            const base64 = await getBase64FromUrl(imageUrl);
            if (base64) imageDataMap[i] = base64;
          }
        }
      }

      setExportTableData({ records, images: imageDataMap });
      await new Promise(r => setTimeout(r, 300));

      if (exportContentRef.current) {
        const canvas = await html2canvas(exportContentRef.current, { scale: 3, useCORS: true, logging: false });
        const imgData = canvas.toDataURL('image/png');

        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width;
        const margin = 5;
        const contentWidth = pageWidth - margin * 2;

        const axoLogoUrl = '/cLogo.png';
        const axoBase64 = await getBase64FromUrl(axoLogoUrl);
        try { doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12); } catch (e) { console.warn("Marabes logo failed", e); }
        if (axoBase64) { doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12); }

        doc.setDrawColor(63, 81, 181);
        doc.setLineWidth(0.5);
        doc.line(margin, 25, pageWidth - margin, 25);

        doc.setFillColor(232, 245, 255);
        doc.rect(margin, 30, contentWidth, 14, 'F');

        const textY = 39;
        doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
        doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, margin + 3, textY);

        doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(40, 58, 139);
        doc.text('In Progress Offboardings Report', pageWidth / 2, textY, { align: 'center' });

        doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
        const recordsText = `Total Records: ${records.length}`;
        const textWidth = doc.getTextWidth(recordsText);
        doc.text(recordsText, margin + contentWidth - 3 - textWidth, textY);

        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const availableHeight = doc.internal.pageSize.height - 48;
        if (imgHeight > availableHeight) {
          const scaleFactor = availableHeight / imgHeight;
          doc.addImage(imgData, 'PNG', margin, 48, imgWidth * scaleFactor, availableHeight);
        } else {
          doc.addImage(imgData, 'PNG', margin, 48, imgWidth, imgHeight);
        }

        doc.save('in-progress-offboardings.pdf');
      }
    } catch (error) {
      console.error('In Progress PDF Export Error:', error);
      alert('Failed to generate PDF');
    } finally {
      setExportTableData(null);
      setIsExportingInProgressPdf(false);
    }
  };

  const inProgressRecords = sortRecords(getFilteredRecords('in-progress'), inProgressSortBy, inProgressSortOrder);
  const completedRecords = sortRecords(getFilteredRecords('completed'), completedSortBy, completedSortOrder);

  const resetFilters = () => {
    setSearchTerm(''); setFilterBy('all'); setSelectedOffice('all');
    setSelectedYear('All'); setSelectedMonth('All'); setDateRange({ start: '', end: '' });
  };

  const OffboardingTableSection = ({
    title, status, icon: Icon, lightBgColor, darkBgColor,
    records, activeTable, setActiveTable,
    handleCheckboxChange, hasUnsavedChanges,
    handleSaveChanges, isSaving, handleEdit, handleDelete,
    tableWrapperRef, formatDateDDMMYYYY, isDark
  }) => {
    const isActive = activeTable === status;
    const headerBg = isDark ? darkBgColor : lightBgColor;

    const renderTableContent = () => (
      <>
        <div className="employees__table-wrapper" ref={isActive ? tableWrapperRef : null}>
          <table className="employees__table">
            <thead>
              <tr className="employees__table-header-row">
                <th className="employees__table-header" style={{ minWidth: '140px' }}>Name</th>
                <th className="employees__table-header" style={{ minWidth: '100px' }}>Department</th>
                <th className="employees__table-header" style={{ minWidth: '90px' }}>Office</th>
                <th className="employees__table-header" style={{ minWidth: '100px' }}>Start Date</th>
                <th className="employees__table-header" style={{ minWidth: '100px' }}>Exit Date</th>
                {/* Set Inactive — first mandatory item */}
                <th key={MANDATORY_ITEMS[0].key} className="employees__table-header" style={{ minWidth: '110px' }}>
                  {MANDATORY_ITEMS[0].label}
                </th>
                {/* Alumni Group — right after Set Inactive */}
                {ALUMNI_ITEMS.map(item => (
                  <th key={item.key} className="employees__table-header" style={{ minWidth: '120px', color: 'var(--primary-color, #3f51b5)' }}>
                    🎓 {item.label}
                  </th>
                ))}
                {/* Remaining mandatory items */}
                {MANDATORY_ITEMS.slice(1).map(item => (
                  <th key={item.key} className="employees__table-header" style={{ minWidth: '110px' }}>
                    {item.label.length > 30 ? item.label.split('(')[0].trim() : item.label}
                  </th>
                ))}
                {OPTIONAL_ITEMS.map(item => (
                  <th key={item.key} className="employees__table-header" style={{ minWidth: '110px', color: '#f59e0b' }}>
                    {item.label}
                  </th>
                ))}
                <th className="employees__table-header" style={{ minWidth: '120px' }}>Overall Progress</th>
                <th className="employees__table-header" style={{ minWidth: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map(record => (
                <tr key={record.id} className="employees__table-row">
                  <td className="employees__table-cell" style={{ fontWeight: 600 }}>{record.employeeName}</td>
                  <td className="employees__table-cell">
                    <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '9999px', background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', color: isDark ? '#e2e8f0' : '#475569' }}>
                      {record.department}
                    </span>
                  </td>
                  <td className="employees__table-cell">
                    <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '9999px', background: 'rgba(63,81,181,0.1)', color: '#3f51b5' }}>
                      {record.office}
                    </span>
                  </td>
                  <td className="employees__table-cell" style={{ fontSize: '0.85rem', color: '#10b981' }}>
                    {formatDateDDMMYYYY(record.dateOfJoining)}
                  </td>
                  <td className="employees__table-cell" style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                    {formatDateDDMMYYYY(record.dateOfDeparture)}
                  </td>
                  {/* Render: Set Inactive → Alumni Group → rest of mandatory → optional */}
                  {[
                    MANDATORY_ITEMS[0],
                    ...ALUMNI_ITEMS,
                    ...MANDATORY_ITEMS.slice(1),
                    ...OPTIONAL_ITEMS
                  ].map(item => {
                    const isAlumni = item.key === 'alumni';
                    return (
                      <td key={item.key} className="employees__table-cell" style={{ textAlign: 'center' }}>
                        <label className="checkbox-wrapper" style={{ display: 'inline-flex', cursor: 'pointer', position: 'relative' }}>
                          <input
                            type="checkbox"
                            checked={record[item.key] || false}
                            onChange={() => handleCheckboxChange(record.id, item.key)}
                            style={{
                              position: 'absolute',
                              opacity: 0,
                              width: '1px',
                              height: '1px',
                              overflow: 'hidden',
                              pointerEvents: 'none'
                            }}
                          />
                          <span
                            className="checkmark"
                            style={{
                              width: '26px',
                              height: '26px',
                              border: isAlumni ? '2px solid var(--primary-color, #3f51b5)' : '2px solid var(--border-color)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: record[item.key]
                                ? (isAlumni ? 'var(--primary-color, #3f51b5)' : 'var(--success-color)')
                                : 'var(--white)',
                              borderColor: record[item.key]
                                ? (isAlumni ? 'var(--primary-color, #3f51b5)' : 'var(--success-color)')
                                : (isAlumni ? 'rgba(63,81,181,0.4)' : 'var(--border-color)'),
                              color: 'var(--white)',
                              transition: 'all 0.2s ease',
                              boxShadow: isAlumni && record[item.key] ? '0 0 0 3px rgba(63,81,181,0.2)' : 'none'
                            }}
                          >
                            {record[item.key] && <Check size={14} />}
                          </span>
                        </label>
                      </td>
                    );
                  })}
                  <td className="employees__table-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                      <div style={{ flex: 1, height: '10px', backgroundColor: 'var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${record.progress}%`,
                          backgroundColor: 'var(--success-color)',
                          borderRadius: '8px',
                          transition: 'width 0.4s ease'
                        }} />
                      </div>
                      <span style={{ fontWeight: '600', color: 'var(--success-color)', minWidth: '40px', fontSize: '0.85rem' }}>
                        {record.progress}%
                      </span>
                    </div>
                  </td>
                  <td className="employees__table-cell" style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEdit(record)}
                        className="btn"
                        style={{ padding: '0.4rem 0.75rem', background: 'var(--primary-color, #3f51b5)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        title="Edit Offboarding"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="btn btn-danger"
                        style={{ padding: '0.4rem 0.75rem', background: 'var(--danger-color, #ef4444)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        title="Delete Offboarding"
                      >
                        <Trash2 size={14} />
                      </button>
                      <button
                        onClick={() => openSTCPreview(record)}
                        disabled={isGeneratingStatement}
                        className="btn"
                        style={{ 
                          padding: '0.4rem 0.75rem', 
                          background: '#10b981', 
                          border: 'none', 
                          borderRadius: '8px', 
                          cursor: isGeneratingStatement ? 'not-allowed' : 'pointer', 
                          color: '#fff', 
                          fontSize: '0.8rem', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          opacity: isGeneratingStatement ? 0.7 : 1
                        }}
                        title="Generate Solde de Tout Compte (FR)"
                      >
                        {isGeneratingStatement ? '...' : <FileText size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isActive && hasUnsavedChanges() && (
          <div className="employees__pagination">
            <div></div>
            <button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </>
    );

    return (
      <div className="employees__table-card" style={{ marginBottom: '1.5rem' }}>
        <button
          className="employees__table-header"
          onClick={() => setActiveTable(status)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '1.5rem 2rem',
            background: headerBg,
            color: isDark ? '#e2e8f0' : '#1e293b',
            border: 'none',
            cursor: 'pointer',
            borderRadius: isActive ? '16px 16px 0 0' : '16px',
            transition: 'border-radius 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Icon size={20} />
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>{title}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ background: 'rgba(0,0,0,0.05)', padding: '0.375rem 0.875rem', borderRadius: '9999px', fontSize: '0.875rem' }}>
              {records.length}
            </span>
            {isActive ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>

        {isActive && (
          <div>
            {records.length > 0 ? renderTableContent() : (
              <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
                <Icon size={48} style={{ marginBottom: '1rem', color: '#cbd5e1' }} />
                <p>No records found</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const checklistFormSections = [
    {
      title: 'Mandatory Checklist (Affects Progress %)',
      items: MANDATORY_ITEMS,
      accentColor: '#22c55e',
    },
    {
      title: 'Optional Checklist (Does NOT Affect Progress)',
      items: OPTIONAL_ITEMS,
      accentColor: '#f59e0b',
    },
    {
      title: 'Additional Options',
      items: ALUMNI_ITEMS,
      accentColor: '#8b5cf6',
      note: formData.alumni ? '✓ This employee will appear in the Alumni Group page (sidebar under Inactive Employees).' : 'Check to add this employee to the Alumni Group.',
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      p: 3,
      bgcolor: 'background.default',
      transition: 'background-color 0.3s'
    }}>
      <PageHeader
        title={<Box display="flex" alignItems="center" gap={1}><LogOut size={24} /> Employee Offboarding</Box>}
        subtitle={'Manage employee offboarding processes and track progress'}
        actions={
          <Box display="flex" gap={1}>
            <ExportButtons
              onExportPDF={() => handleExportClick('pdf')}
              onExportExcel={() => handleExportClick('excel')}
              isExporting={isExportingPdf}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus size={16} />}
              onClick={handleAddNew}
              disabled={isLoading}
            >
              Add Offboarding
            </Button>
          </Box>
        }
      />

      {errorMessage && !showForm && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertCircle size={20} />
          <Typography>{errorMessage}</Typography>
        </Box>
      )}

      <Card sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1} color="primary">
            <Filter size={20} /> Filters
          </Typography>
          <Button color="error" variant="outlined" startIcon={<RotateCcw size={16} />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Reset All</Button>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <div className="axo-search-container">
              <div className="axo-search-icon">
                <Search size={18} />
              </div>
              <input
                type="text"
                className="axo-search-input"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <Users
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '38px',
                  borderColor: filterBy !== 'all' ? '#1976d2' : undefined,
                  fontWeight: filterBy !== 'all' ? 600 : undefined
                }}
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
              >
                {getFilteredDepartments().map(dept => (
                  <option key={dept} value={dept}>
                    {formatUnderscoredText(dept) === 'All' ? 'All Departments' : formatUnderscoredText(dept)}
                  </option>
                ))}
              </select>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <MapPin
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '38px',
                  borderColor: selectedOffice !== 'all' ? '#1976d2' : undefined,
                  fontWeight: selectedOffice !== 'all' ? 600 : undefined
                }}
                value={selectedOffice}
                onChange={(e) => setSelectedOffice(e.target.value)}
              >
                {getFilteredOffices().map(office => (
                  <option key={office} value={office}>
                    {formatUnderscoredText(office) === 'All' ? 'All Offices' : formatUnderscoredText(office)}
                  </option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={6} md={1}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <Calendar
                size={14}
                style={{
                  position: 'absolute',
                  left: '8px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '28px',
                  borderColor: selectedYear !== 'All' ? '#1976d2' : undefined,
                  fontWeight: selectedYear !== 'All' ? 600 : undefined
                }}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year === 'All' ? 'All Years' : year}</option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={6} md={2}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <Calendar
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '38px',
                  borderColor: selectedMonth !== 'All' ? '#1976d2' : undefined,
                  fontWeight: selectedMonth !== 'All' ? 600 : undefined
                }}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {MONTH_NAMES.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant={showAuditInfo ? "contained" : "outlined"}
              onClick={() => setShowAuditInfo(!showAuditInfo)}
              startIcon={<Clock size={16} />}
              fullWidth
              sx={{ height: '43px', textTransform: 'none', borderRadius: '8px' }}
            >
              {showAuditInfo ? 'Hide' : 'Show'} Audit Info
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="warning"
            startIcon={<FileText size={16} />}
            onClick={exportInProgressOffboardingPDF}
            disabled={isExportingInProgressPdf}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            {isExportingInProgressPdf ? 'Generating...' : 'In Progress Offboardings'}
          </Button>
        </Box>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Box className="spinner"></Box>
          <Typography>Loading offboarding data...</Typography>
        </Box>
      )}

      {!isLoading && (
        <div className="employees__table-section">
          <OffboardingTableSection
            title={`Offboardings in Progress`}
            status="in-progress"
            icon={Clock}
            lightBgColor="#f0f7ff"
            darkBgColor="#1e2d3d"
            records={inProgressRecords}
            activeTable={activeTable}
            setActiveTable={setActiveTable}
            handleCheckboxChange={handleCheckboxChange}
            hasUnsavedChanges={hasUnsavedChanges}
            handleSaveChanges={handleSaveChanges}
            isSaving={isSaving}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            tableWrapperRef={tableWrapperRef}
            formatDateDDMMYYYY={formatDateDDMMYYYY}
            isDark={isDark}
          />

          <OffboardingTableSection
            title={`Completed Offboardings`}
            status="completed"
            icon={CheckCircle}
            lightBgColor="#f0fdf4"
            darkBgColor="#1a2e20"
            records={completedRecords}
            activeTable={activeTable}
            setActiveTable={setActiveTable}
            handleCheckboxChange={handleCheckboxChange}
            hasUnsavedChanges={hasUnsavedChanges}
            handleSaveChanges={handleSaveChanges}
            isSaving={isSaving}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            tableWrapperRef={tableWrapperRef}
            formatDateDDMMYYYY={formatDateDDMMYYYY}
            isDark={isDark}
          />
        </div>
      )}

      {showForm && (
        <div className="form-overlay" onClick={() => setShowForm(false)}>
          <div className="form-modal" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowForm(false)}><X size={20} /></button>
            <div className="form-section" style={{ padding: '0' }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: theme.palette?.primary?.main || '#1e293b', marginBottom: '1.5rem', padding: '0 0.5rem' }}>
                {editingId ? <><Edit size={20} /> Edit Offboarding</> : <><Plus size={20} /> New Offboarding</>}
              </h2>
              <form onSubmit={handleSubmit}>

                <div className="uf-form-section" style={{ position: 'relative', zIndex: 10 }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={18} /> Employee Information
                  </h3>

                  <div className="uf-form-grid">
                    <div className="uf-form-group" ref={dropdownRef} style={{ position: 'relative' }}>
                      <label className="uf-form-label">Select Employee <span className="required">*</span></label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={`uf-form-input ${formErrors.employeeId ? 'error' : ''}`}
                          style={{ paddingRight: '2.5rem' }}
                          placeholder={editingId ? formData.employeeName : "Click to select or type to search..."}
                          value={editingId ? formData.employeeName : empSearchQuery}
                          onChange={(e) => {
                            if (editingId) return;
                            const val = e.target.value;
                            setEmpSearchQuery(val);
                            setIsDropdownOpen(true);
                            if (!val) {
                              handleInputChange('employeeId', '');
                            }
                          }}
                          onFocus={() => {
                            if (editingId) return;
                            setIsDropdownOpen(true);
                            // Clear input if it is exactly the currently selected employee name so they can search easily
                            const selectedEmp = findEmployeeByIdentifier(formData.employeeId);
                            const selectedName = selectedEmp ? getEmployeeFullName(selectedEmp) : '';
                            if (empSearchQuery === selectedName) {
                              setEmpSearchQuery('');
                            }
                          }}
                          disabled={!!editingId}
                        />
                        <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                          <Search size={16} style={{ color: '#94a3b8' }} />
                        </div>
                      </div>
                      {formErrors.employeeId && <div className="uf-form-error-msg">{formErrors.employeeId}</div>}

                      {/* Floating custom searchable select dropdown list */}
                      {isDropdownOpen && !editingId && (
                        <div
                          className="search-dropdown-list"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            marginTop: '6px',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            border: '1px solid',
                            borderColor: isDark ? '#334155' : '#cbd5e1',
                            borderRadius: '8px',
                            boxShadow: isDark
                              ? '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)'
                              : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                        >
                          {filteredActiveEmployees.length === 0 && filteredInactiveEmployees.length === 0 ? (
                            <div style={{ padding: '12px', color: '#64748b', fontSize: '0.875rem', textAlign: 'center' }}>
                              Aucun employé trouvé
                            </div>
                          ) : (
                            <>
                              {filteredActiveEmployees.map(emp => {
                                const isSelected = formData.employeeId === emp.employeeId;
                                return (
                                  <div
                                    key={emp.employeeId}
                                    onClick={() => {
                                      handleInputChange('employeeId', emp.employeeId);
                                      setEmpSearchQuery(`${emp.firstName || ''} ${emp.lastName || ''}`.trim());
                                      setIsDropdownOpen(false);
                                    }}
                                    className={`search-dropdown-item ${isSelected ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      height: '40px',
                                      boxSizing: 'border-box',
                                      padding: '0 14px',
                                      fontSize: '0.9rem',
                                      cursor: 'pointer',
                                      color: isSelected
                                        ? (isDark ? '#93c5fd' : '#1e40af')
                                        : (isDark ? '#f1f5f9' : '#0f172a'),
                                      backgroundColor: isSelected
                                        ? (isDark ? '#1e3a8a' : '#eff6ff')
                                        : 'transparent',
                                      transition: 'all 0.15s ease',
                                      borderBottom: '1px solid',
                                      borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'
                                    }}
                                  >
                                    <span style={{ fontWeight: '500' }}>
                                      {emp.firstName} {emp.lastName}
                                    </span>
                                  </div>
                                );
                              })}
                              {filteredInactiveEmployees.map(emp => {
                                const empId = emp.employeeId || emp.id;
                                const isSelected = formData.employeeId === empId;
                                return (
                                  <div
                                    key={empId}
                                    onClick={() => {
                                      handleInputChange('employeeId', empId);
                                      setEmpSearchQuery(`${emp.firstName || ''} ${emp.lastName || ''} (Inactif)`.trim());
                                      setIsDropdownOpen(false);
                                    }}
                                    className={`search-dropdown-item ${isSelected ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      height: '40px',
                                      boxSizing: 'border-box',
                                      padding: '0 14px',
                                      fontSize: '0.9rem',
                                      cursor: 'pointer',
                                      color: isSelected
                                        ? (isDark ? '#93c5fd' : '#1e40af')
                                        : (isDark ? '#cbd5e1' : '#475569'),
                                      backgroundColor: isSelected
                                        ? (isDark ? '#1e3a8a' : '#eff6ff')
                                        : 'transparent',
                                      transition: 'all 0.15s ease',
                                      borderBottom: '1px solid',
                                      borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'
                                    }}
                                  >
                                    <span style={{ fontWeight: '500', opacity: 0.85 }}>
                                      {emp.firstName} {emp.lastName}
                                    </span>
                                    <span style={{
                                      marginLeft: 'auto',
                                      fontSize: '0.7rem',
                                      backgroundColor: isDark ? '#7f1d1d' : '#fee2e2',
                                      color: isDark ? '#fca5a5' : '#ef4444',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      Inactif
                                    </span>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Date of Departure <span className="required">*</span></label>
                      <input
                        type="date"
                        className={`uf-form-input ${formErrors.dateOfDeparture ? 'error' : ''}`}
                        value={formData.dateOfDeparture}
                        onChange={(e) => handleInputChange('dateOfDeparture', e.target.value)}
                        required
                      />
                      {formErrors.dateOfDeparture && <div className="uf-form-error-msg">{formErrors.dateOfDeparture}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Department</label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.department || ''}
                        disabled
                        style={{ backgroundColor: theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9', cursor: 'not-allowed' }}
                      />
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Office</label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.office || ''}
                        disabled
                        style={{ backgroundColor: theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>
                </div>

                {checklistFormSections.map((section, idx) => (
                  <div key={idx} className="uf-form-section" style={{ backgroundColor: theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.02)' : idx === 2 ? 'rgba(139,92,246,0.03)' : 'rgba(0,0,0,0.01)', padding: '1.25rem', borderRadius: '8px', border: `1px solid ${idx === 2 ? 'rgba(139,92,246,0.2)' : theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: section.accentColor, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {idx === 0 ? '✅' : idx === 1 ? '⭐' : '🎓'} {section.title}
                    </h3>
                    <div className="uf-form-grid" style={{ gap: '12px' }}>
                      {section.items.map(item => (
                        <div key={item.key} className="uf-form-group full-width">
                          <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            cursor: 'pointer',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            backgroundColor: item.key === 'alumni' && formData.alumni
                              ? (theme.palette?.mode === 'dark' ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.06)')
                              : (theme.palette?.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#ffffff'),
                            border: `1px solid ${item.key === 'alumni' && formData.alumni ? 'rgba(139,92,246,0.4)' : (theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e2e8f0')}`,
                            transition: 'all 0.2s ease'
                          }}>
                            <input
                              type="checkbox"
                              checked={Boolean(formData[item.key])}
                              onChange={(e) => handleInputChange(item.key, e.target.checked)}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                marginTop: '2px',
                                accentColor: section.accentColor
                              }}
                            />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.palette?.mode === 'dark' ? '#cbd5e1' : '#334155' }}>
                                <item.icon size={16} style={{ color: section.accentColor }} />
                                <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{item.label}</span>
                              </div>
                              {item.key === 'alumni' && (
                                <span style={{ fontSize: '0.78rem', color: formData.alumni ? '#8b5cf6' : (theme.palette?.mode === 'dark' ? '#64748b' : '#94a3b8'), paddingLeft: '24px' }}>
                                  {section.note}
                                </span>
                              )}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="uf-form-section">
                  <div className="uf-form-group full-width" style={{ marginBottom: '1rem' }}>
                    <label className="uf-form-label">Reason for Departure <span className="required">*</span></label>
                    <RichTextEditor
                      value={formData.reason || ''}
                      onChange={(val) => handleInputChange('reason', val)}
                    />
                    {formErrors.reason && <div className="uf-form-error-msg">{formErrors.reason}</div>}
                  </div>

                  <div className="uf-form-group full-width">
                    <label className="uf-form-label">Additional Notes</label>
                    <RichTextEditor
                      value={formData.notes || ''}
                      onChange={(val) => handleInputChange('notes', val)}
                    />
                  </div>
                </div>

                <div className="uf-form-actions">
                  {errorMessage && (
                    <div style={{
                      width: '100%',
                      marginBottom: '12px',
                      padding: '12px 16px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fca5a5',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      color: '#991b1b'
                    }}>
                      <AlertCircle size={18} style={{ marginTop: '1px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '0.875rem', marginBottom: '2px' }}>Unable to Save</div>
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>{errorMessage}</div>
                      </div>
                    </div>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Record'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* STC Preview Dialog */}
      {/* STC Preview Dialog */}
      <Dialog
        open={stcDialog.open && !!stcPreview}
        onClose={() => { setStcDialog({ open: false, record: null }); setStcPreview(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '1.4rem', pb: 1, color: '#283a8b' }}>
          📋 Aperçu du Solde de Tout Compte (STC)
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {stcPreview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Tab selector */}
              <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Button
                  onClick={() => setStcTab('form')}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    fontWeight: 'bold',
                    borderBottom: stcTab === 'form' ? '3px solid #283a8b' : 'none',
                    color: stcTab === 'form' ? '#283a8b' : '#64748b',
                    borderRadius: 0,
                    textTransform: 'none',
                    fontSize: '0.95rem'
                  }}
                >
                  📄 Aperçu Officiel
                </Button>
                <Button
                  onClick={() => setStcTab('calculations')}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    fontWeight: 'bold',
                    borderBottom: stcTab === 'calculations' ? '3px solid #283a8b' : 'none',
                    color: stcTab === 'calculations' ? '#283a8b' : '#64748b',
                    borderRadius: 0,
                    textTransform: 'none',
                    fontSize: '0.95rem'
                  }}
                >
                  🧮 Détails des Calculs
                </Button>
                <Button
                  onClick={() => setStcTab('edit')}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    fontWeight: 'bold',
                    borderBottom: stcTab === 'edit' ? '3px solid #283a8b' : 'none',
                    color: stcTab === 'edit' ? '#283a8b' : '#64748b',
                    borderRadius: 0,
                    textTransform: 'none',
                    fontSize: '0.95rem'
                  }}
                >
                  ✏️ Corriger les Valeurs
                </Button>
              </Box>

              {stcTab === 'form' && (
                /* OFFICIAL STC FORM IN FRENCH (MATCHES EXACT WORD STRUCTURE) */
                <Box sx={{ p: 4, border: '1px solid #cbd5e1', borderRadius: 2, bgcolor: '#ffffff', color: '#0f172a', fontFamily: 'serif', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'normal', textAlign: 'center', mb: 2, fontFamily: 'serif', color: '#000000', letterSpacing: 0.5 }}>
                    Solde de Tout Compte
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', color: '#000000' }}>
                      Nom et prénom : <strong>{stcPreview.employeeName}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', color: '#000000' }}>
                      CIN : <strong>{stcPreview.idNumber || 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', color: '#000000' }}>
                      Poste occupé : <strong>{stcPreview.positions?.join(', ') || 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', color: '#000000' }}>
                      Date d’entrée : <strong>{formatDateFR(stcPreview.startDate) || 'N/A'}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', color: '#000000' }}>
                      Date de sortie : <strong>{formatDateFR(stcPreview.exitDate) || 'N/A'}</strong>
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'normal', mb: 0.5, fontFamily: 'serif', fontSize: '1.15rem', color: '#000000', borderBottom: '1px solid #000000', pb: 0.5 }}>
                    Détails du solde de tout compte
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Salaire du dernier mois :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.workedAmount.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Heures supplémentaires :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.extraHoursPayment.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Indemnité de congés payés :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.vacationDeduction < 0 ? Math.abs(stcPreview.vacationDeduction).toFixed(2) : '0.00'}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Retenue pour congés pris en excès :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.vacationDeduction > 0 ? stcPreview.vacationDeduction.toFixed(2) : '0.00'}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Retenue absences non payées :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.unpaidDeduction.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Indemnité de préavis :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.noticePay.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Indemnité de licenciement :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.severancePay.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>Autres primes :</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                        <strong>{stcPreview.otherBonuses.toFixed(2)}</strong> DH
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'normal', mt: 1, mb: 0.5, fontFamily: 'serif', fontSize: '1.15rem', color: '#000000' }}>
                    Montant total dû
                  </Typography>
                  
                  <Box sx={{ p: 2, border: '1px solid #000000', borderRadius: 1, bgcolor: '#f8fafc' }}>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', lineHeight: 1.5, color: '#000000' }}>
                      {stcPreview.totalFinalPay >= 0 ? (
                        <>Montant total net dû à l’employé après calcul de tous les éléments de rémunération et déduction des retenues applicables : +<strong>{stcPreview.totalFinalPay.toFixed(2)}</strong> DH</>
                      ) : (
                        <>Montant total net à rembourser par l’employé après calcul de tous les éléments de rémunération et déduction des retenues applicables : −<strong>{Math.abs(stcPreview.totalFinalPay).toFixed(2)}</strong> DH</>
                      )}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ fontFamily: 'serif', fontStyle: 'italic', color: '#475569', mb: 1 }}>
                    Sauf erreur, omission ou opérations en cours.
                  </Typography>

                  <Typography variant="subtitle1" sx={{ fontWeight: 'normal', mb: 0.5, fontFamily: 'serif', fontSize: '1.15rem', color: '#000000' }}>
                    Attestation de réception
                  </Typography>

                  <Typography variant="body1" sx={{ fontFamily: 'serif', fontSize: '1.05rem', mb: 2, lineHeight: 1.6, color: '#000000' }}>
                    Je soussigné <strong>{stcPreview.employeeName}</strong> reconnais avoir reçu de la part de MARABES GROUP le montant susmentionné en règlement de mon solde de tout compte.
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', color: '#000000' }}>
                      Fait à Nador, le <strong>{formatDateFR(new Date())}</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'serif', textAlign: 'right', color: '#000000' }}>
                      Signature de l’employé :
                    </Typography>
                  </Box>
                </Box>
              )}

              {stcTab === 'calculations' && (
                /* DETAILED STEP-BY-STEP MATHEMATICAL FORMULA BREAKDOWN IN ENGLISH */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Rates Calculation Card */}
                  <Box sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: 3, border: '1px solid #fde68a', boxShadow: '0 2px 4px rgba(217, 119, 6, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#d97706', fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      📊 Salary Rates
                    </Typography>
                    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        5. Daily Rate: Formula: <code>Monthly Salary ÷ Worked Days</code>. Calculation: <code>{stcPreview.lastMonthSalary.toFixed(2)} ÷ 26</code> = <strong>{stcPreview.dailyRate.toFixed(2)}</strong> MAD/day
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        6. Hourly Rate: Formula: <code>Daily Rate ÷ 8</code>. Calculation: <code>{stcPreview.dailyRate.toFixed(2)} ÷ 8</code> = <strong>{stcPreview.hourlyRate.toFixed(2)}</strong> MAD/hour
                      </Typography>
                    </Box>
                  </Box>
 
                  {/* Calculation Details Card */}
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 800, mb: 2, borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                      💼 Detailed Formula Breakdown
                    </Typography>
 
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Base Salary */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#475569' }}>Contractual Monthly Salary</Typography>
                        <Typography variant="body2"><strong>{stcPreview.lastMonthSalary.toFixed(2)}</strong> MAD</Typography>
                      </Box>
 
                      {/* Worked Hours & Worked Amount */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>7. Worked Amount (Salaire du dernier mois)</Typography>
                          <Typography variant="body2" sx={{ color: '#16a34a' }}>+<strong>{stcPreview.workedAmount.toFixed(2)}</strong> MAD</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5 }}>
                          • 1. Worked Hours: Formula: <code>Worked Days × 8</code>. Calculation: <code>{stcPreview.workedDays} × 8</code> = <strong>{stcPreview.workedHours}</strong> hours
                        </Typography>
                        {stcPreview.workedDays >= 26 ? (
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            • Formula: <code>Contractual Monthly Salary</code>. Calculation: <code>{stcPreview.lastMonthSalary.toFixed(2)}</code> = <strong>{stcPreview.workedAmount.toFixed(2)}</strong> MAD
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            • Formula: <code>Worked Hours × Hourly Rate</code>. Calculation: <code>{stcPreview.workedHours} × {stcPreview.hourlyRate.toFixed(2)}</code> = <strong>{stcPreview.workedAmount.toFixed(2)}</strong> MAD
                          </Typography>
                        )}
                      </Box>
 
                      {/* Vacation Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>8. Vacation Deduction</Typography>
                          <Typography variant="body2" sx={{ color: stcPreview.vacationDeduction >= 0 ? '#dc2626' : '#16a34a' }}>
                            {stcPreview.vacationDeduction >= 0 ? '-' : '+'}<strong>{Math.abs(stcPreview.vacationDeduction).toFixed(2)}</strong> MAD
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1, borderLeft: '2px solid #cbd5e1', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: '#475569' }}>
                            • 2. Vacation Hours Taken: Formula: <code>Vacation Days Taken × 8</code>. Calculation: <code>{stcPreview.vacationTakenDays} × 8</code> = <strong>{stcPreview.vacationHoursTaken}</strong> hours
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#475569' }}>
                            • 3. Allowed Vacation Hours: Formula: <code>Allowed Vacation Days × 8</code>. Calculation: <code>{stcPreview.vacationAllowedDays} × 8</code> = <strong>{stcPreview.allowedVacationHours}</strong> hours
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>
                            • 4. Extra Vacation Hours: Formula: <code>(Vacation Taken Days − Allowed Vacation Days) × 8</code>. Calculation: <code>({stcPreview.vacationTakenDays} − {stcPreview.vacationAllowedDays}) × 8</code> = <strong>{stcPreview.extraVacationHours}</strong> hours
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          • Formula: <code>Extra Vacation Hours × Hourly Rate</code>. Calculation: <code>{stcPreview.extraVacationHours} × {stcPreview.hourlyRate.toFixed(2)}</code> = <strong>{stcPreview.vacationDeduction.toFixed(2)}</strong> MAD
                        </Typography>
                      </Box>
 
                      {/* Unpaid Deduction Details (Unpaid Absences) */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>9. Unpaid Deduction</Typography>
                          <Typography variant="body2" sx={{ color: '#dc2626' }}>-<strong>{stcPreview.unpaidDeduction.toFixed(2)}</strong> MAD</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5 }}>
                          • Unpaid Absence Hours: Formula: <code>Unpaid Absence Days × 8</code>. Calculation: <code>{stcPreview.unpaidAbsenceDays} × 8</code> = <strong>{stcPreview.unpaidAbsenceHours}</strong> hours
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          • Formula: <code>Unpaid Absence Hours × Hourly Rate</code>. Calculation: <code>{stcPreview.unpaidAbsenceHours} × {stcPreview.hourlyRate.toFixed(2)}</code> = <strong>{stcPreview.unpaidDeduction.toFixed(2)}</strong> MAD
                        </Typography>
                      </Box>
 
                      {/* Extra Hours Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>10. Extra Hours Payment</Typography>
                          <Typography variant="body2" sx={{ color: '#16a34a' }}>+<strong>{stcPreview.extraHoursPayment.toFixed(2)}</strong> MAD</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          • Formula: <code>Extra Hours × Hourly Rate</code>. Calculation: <code>{stcPreview.extraHours} × {stcPreview.hourlyRate.toFixed(2)}</code> = <strong>{stcPreview.extraHoursPayment.toFixed(2)}</strong> MAD
                        </Typography>
                      </Box>

                      {/* Notice, Severance, Bonuses */}
                      {(!!stcPreview.noticePay || !!stcPreview.severancePay || !!stcPreview.otherBonuses) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                          {!!stcPreview.noticePay && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#475569' }}>Notice Pay</Typography>
                              <Typography variant="body2" sx={{ color: '#16a34a' }}>+<strong>{stcPreview.noticePay.toFixed(2)}</strong> MAD</Typography>
                            </Box>
                          )}
                          {!!stcPreview.severancePay && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#475569' }}>Severance Pay</Typography>
                              <Typography variant="body2" sx={{ color: '#16a34a' }}>+<strong>{stcPreview.severancePay.toFixed(2)}</strong> MAD</Typography>
                            </Box>
                          )}
                          {!!stcPreview.otherBonuses && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#475569' }}>Other Bonuses</Typography>
                              <Typography variant="body2" sx={{ color: '#16a34a' }}>+<strong>{stcPreview.otherBonuses.toFixed(2)}</strong> MAD</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
 
                  {/* Mathematical Formula Banner */}
                  <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2.5, border: '1px dashed #94a3b8' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontStyle: 'italic', textAlign: 'center', lineHeight: 1.4 }}>
                      Global Formula: Worked Amount (<strong>{stcPreview.workedAmount.toFixed(2)}</strong> MAD) − Vacation Deduction (<strong>{stcPreview.vacationDeduction.toFixed(2)}</strong> MAD) − Unpaid Deduction (<strong>{stcPreview.unpaidDeduction.toFixed(2)}</strong> MAD)
                      {stcPreview.extraHoursPayment > 0 && ` + Extra Hours Payment (${stcPreview.extraHoursPayment.toFixed(2)} MAD)`}
                      {!!stcPreview.noticePay && ` + Notice Pay (${stcPreview.noticePay.toFixed(2)} MAD)`}
                      {!!stcPreview.severancePay && ` + Severance Pay (${stcPreview.severancePay.toFixed(2)} MAD)`}
                      {!!stcPreview.otherBonuses && ` + Other Bonuses (${stcPreview.otherBonuses.toFixed(2)} MAD)`}
                    </Typography>
                  </Box>
 
                  {/* Total Card */}
                  <Box sx={{ p: 2.5, bgcolor: stcPreview.totalFinalPay >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 3, border: `1px solid ${stcPreview.totalFinalPay >= 0 ? '#bbf7d0' : '#fca5a5'}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.5, fontWeight: 600 }}>11. Final Settlement (Net Payout)</Typography>
                    <Typography variant="h5" sx={{ color: stcPreview.totalFinalPay >= 0 ? '#15803d' : '#b91c1c', my: 1 }}>
                      {stcPreview.totalFinalPay >= 0 ? '+' : '−'}<strong style={{ fontWeight: 900 }}>{Math.abs(stcPreview.totalFinalPay).toFixed(2)}</strong> MAD
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: stcPreview.totalFinalPay >= 0 ? '#166534' : '#991b1b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                      {stcPreview.totalFinalPay >= 0 ? (
                        <>The company will pay this amount to <strong>{stcPreview.employeeName}</strong></>
                      ) : (
                        <><strong>{stcPreview.employeeName}</strong> must reimburse this amount to the company</>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}

              {stcTab === 'edit' && (
                /* EDIT VALUES TAB - ALLOWS CORRECTION OF ALL STC VALUES */
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Typography variant="body2" sx={{ color: '#475569', mb: 1, backgroundColor: '#f1f5f9', p: 1.5, borderRadius: 2, borderLeft: '4px solid #283a8b', fontSize: '0.9rem', lineHeight: 1.4 }}>
                    💡 <strong>Correction interactive :</strong> Modifiez les données ci-dessous pour rectifier les calculs du Solde de Tout Compte. Les modifications impactent instantanément l'aperçu officiel, les formules détaillées ainsi que les documents PDF et Word générés.
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Row 1: Salary & Overtime */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Salaire Mensuel Contractuel (MAD)
                        </Typography>
                        <input
                          type="number"
                          step="0.01"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.lastMonthSalary || 0}
                          onChange={(e) => handleStcValueChange('lastMonthSalary', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Salaire brut de base contractuel
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Heures Supplémentaires (Heures)
                        </Typography>
                        <input
                          type="number"
                          step="0.5"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.extraHours || 0}
                          onChange={(e) => handleStcValueChange('extraHours', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Nombre d'heures supplémentaires
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Row 2: Allowed & Taken Vacation */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Congés Payés Dus (Jours)
                        </Typography>
                        <input
                          type="number"
                          step="0.5"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.vacationAllowedDays || 0}
                          onChange={(e) => handleStcValueChange('vacationAllowedDays', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Congés acquis non encore consommés
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Congés pris en excès (Jours)
                        </Typography>
                        <input
                          type="number"
                          step="0.5"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.vacationTakenDays || 0}
                          onChange={(e) => handleStcValueChange('vacationTakenDays', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Congés consommés au-delà du solde
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Row 3: Unpaid Absences & Notice Pay */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Retenue Absences Non Payées (Jours)
                        </Typography>
                        <input
                          type="number"
                          step="0.5"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.unpaidAbsenceDays || 0}
                          onChange={(e) => handleStcValueChange('unpaidAbsenceDays', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Total des absences non rémunérées
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Indemnité de Préavis (MAD)
                        </Typography>
                        <input
                          type="number"
                          step="0.01"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.noticePay || 0}
                          onChange={(e) => handleStcValueChange('noticePay', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Indemnité forfaitaire de préavis
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Row 4: Severance Pay & Other Bonuses */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Indemnité de Licenciement (MAD)
                        </Typography>
                        <input
                          type="number"
                          step="0.01"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.severancePay || 0}
                          onChange={(e) => handleStcValueChange('severancePay', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Indemnité légale/conventionnelle de licenciement
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                          Autres Primes / Ajustements (MAD)
                        </Typography>
                        <input
                          type="number"
                          step="0.01"
                          className="uf-form-input"
                          style={{ width: '100%' }}
                          value={stcPreview.otherBonuses || 0}
                          onChange={(e) => handleStcValueChange('otherBonuses', e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Ajustements de rémunération divers
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Estimated Final Pay Banner inside Edit Tab */}
                  <Box sx={{ p: 2, bgcolor: stcPreview.totalFinalPay >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 2.5, border: `1px solid ${stcPreview.totalFinalPay >= 0 ? '#bbf7d0' : '#fca5a5'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 'bold' }}>
                        Nouveau Solde Net Estimé
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Calculé avec les valeurs corrigées
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: stcPreview.totalFinalPay >= 0 ? '#15803d' : '#b91c1c', fontWeight: 'bold' }}>
                      {stcPreview.totalFinalPay >= 0 ? '+' : '−'}{Math.abs(stcPreview.totalFinalPay).toFixed(2)} MAD
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, flexDirection: 'column', alignItems: 'stretch' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<FileText size={18} />}
              onClick={() => generateSTCPDF(stcPreview)}
              fullWidth
              sx={{ py: 1.3, fontWeight: 'bold', borderRadius: 2 }}
              disabled={isGeneratingStatement}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<FileText size={18} />}
              onClick={() => generateSTCWord(stcPreview)}
              fullWidth
              sx={{ py: 1.3, fontWeight: 'bold', borderRadius: 2, backgroundColor: '#2b579a', '&:hover': { backgroundColor: '#1e3f73' } }}
              disabled={isGeneratingStatement}
            >
              Export Word
            </Button>
          </Box>
          <Button
            onClick={() => { setStcDialog({ open: false, record: null }); setStcPreview(null); }}
            color="inherit"
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportDialog.open} onClose={() => setExportDialog({ open: false, type: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Export Records</DialogTitle>
        <DialogContent>
          <Typography>
            Based on your current filters, you have both <strong>Completed</strong> and <strong>In Progress</strong> records. Which would you like to export?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1.5, p: 3, alignItems: 'stretch' }}>
          <Button variant="contained" color="primary" onClick={() => handleExportConfirm('all')} fullWidth sx={{ py: 1.2, fontWeight: 'bold' }}>
            Export All Matching Records
          </Button>
          <Button variant="outlined" color="success" onClick={() => handleExportConfirm('completed')} fullWidth sx={{ py: 1, borderWidth: '2px', '&:hover': { borderWidth: '2px' } }}>
            Export Completed Only
          </Button>
          <Button variant="outlined" onClick={() => handleExportConfirm('in-progress')} fullWidth sx={{ py: 1, borderWidth: '2px', color: '#d97706', borderColor: '#fbbf24', '&:hover': { borderWidth: '2px', borderColor: '#f59e0b', backgroundColor: '#fffbeb' } }}>
            Export In Progress Only
          </Button>
          <Button color="inherit" onClick={() => setExportDialog({ open: false, type: '' })} fullWidth sx={{ mt: 1 }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {exportTableData && (
        <div ref={exportContentRef} style={{ position: 'absolute', left: '-9999px', top: 0, width: '1200px', zIndex: -1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif' }}>
            <thead>
              <tr style={{ backgroundColor: '#1976d2', color: 'white' }}>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)', width: '70px' }}>Picture</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Name</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Position</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Project</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Offboarding Reason</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.2)', width: '100px' }}>Start Date</th>
                <th style={{ padding: '8px 10px', fontSize: '11px', textAlign: 'left', fontWeight: 'bold', width: '100px' }}>End Date</th>
              </tr>
            </thead>
            <tbody>
              {exportTableData.records.map((record, i) => {
                const empId = resolveOffboardingEmployeeId(record);
                const employee = findEmployeeByIdentifier(empId);
                const imgSrc = exportTableData.images[i];
                const position = employee?.positions?.[0]?.positionName || employee?.position || 'N/A';
                const project = employee?.projects?.[0]?.projectName || employee?.projects?.[0] || employee?.projectName || 'N/A';
                return (
                  <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#f5f5f5' : 'white', borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '6px 8px', fontSize: '10px', textAlign: 'center' }}>
                      {imgSrc ? <img src={imgSrc} alt="" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} /> : ''}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', borderRight: '1px solid #e0e0e0' }}>{record.employeeName}</td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', borderRight: '1px solid #e0e0e0' }}>{position}</td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', borderRight: '1px solid #e0e0e0' }}>{project}</td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', borderRight: '1px solid #e0e0e0' }}>{record.reason}</td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', borderRight: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatDateDDMMYYYY(record.dateOfJoining)}</td>
                    <td style={{ padding: '6px 8px', fontSize: '10px', whiteSpace: 'nowrap' }}>{formatDateDDMMYYYY(record.dateOfDeparture)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Box>
  );
};

export default Offboarding;
